import { useState, useEffect, useCallback, useRef } from 'react';
import { renderMarkdown } from '../lib/markdown';
import { useAuthStore } from '../features/auth/auth-store';
import { validateCefrDoc, patternsReady, getTranscriptionPatterns, getTranscriptionInfo, type ValidationResult, type TranscriptionPatternsConfig } from '../lib/cefr-validator';
import { langName, setCustomLangName } from '../lib/lang-names';

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? 'https://ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com';

interface CefrDocsProps { level?: string; }
type DocStatus = 'draft' | 'approved' | 'none';
interface CellState { status: DocStatus; source: 'db' | null; }

interface RatingScores {
  overall: number;
  accuracy?: number;
  coverage?: number;
  quantity?: number;
  transcription?: number;
}

interface FullRating {
  overall: number;
  accuracy: { score: number; issues: string[] };
  coverage: { score: number; issues: string[] };
  quantity: { score: number; issues: string[] };
  transcription: { score: number; issues: string[] };
  summary: string;
}

interface GeneratedDoc {
  key: string;
  language_code: string;
  level: string;
  base_lang?: string;
  status: 'draft' | 'approved';
  rating?: RatingScores | null;
}

interface LogEntry {
  id: number;
  ts: string;
  type: 'request' | 'response' | 'error' | 'info';
  method?: string;
  url?: string;
  status?: number;
  durationMs?: number;
  requestBody?: string;
  responseBody?: string;
  message?: string;
}

interface FixQaRunStatus {
  doc_key: string;
  status: 'scheduled' | 'running' | 'completed' | 'failed' | 'cancelled';
  current_step: number;
  total_steps: number;
  current_section: string;
  current_check: string;
  cost_usd: number;
  started_at: string | null;
  completed_at: string | null;
  scheduled_at: string | null;
  error: string | null;
  language_code: string;
  level: string;
  created_at: string;
  diagnostics?: {
    failed_step: number;
    failed_section: string;
    error_message: string;
    input_tokens: number;
    output_tokens: number;
    prompt_excerpt: string;
  };
  steps?: Array<{
    step_index: number;
    step_type: string;
    section: string;
    label: string;
    status: string;
    cost_usd: number;
    input_tokens: number;
    output_tokens: number;
    error: string | null;
  }>;
}

interface LocRunStatus extends FixQaRunStatus {
  target_base_lang: string;
  source_base_lang: string;
  run_type: 'localization';
}

const LEVELS = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

// Source (base) language for each target language's CEFR docs.
// Most languages are produced from English; ENG varieties are produced from Japanese.
const BASE_LANG_MAP: Record<string, string[]> = {
  ENG_AU: ['ja'],
  ENG_GB: ['ja'],
  ENG_US: ['ja'],
};
const DEFAULT_BASE_LANGS = ['en'];

function getBaseLangs(langCode: string): string[] {
  return BASE_LANG_MAP[langCode] ?? DEFAULT_BASE_LANGS;
}

const ALL_BASE_LANGS = [
  { code: 'en', label: 'English' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh-TW', label: 'Chinese (TW)' },
  { code: 'es-ES', label: 'Spanish (Castilian)' },
  { code: 'pt-BR', label: 'Portuguese (Brazil)' },
  { code: 'fr', label: 'French' },
  { code: 'ar', label: 'Arabic (MSA)' },
  { code: 'ru', label: 'Russian' },
  { code: 'th', label: 'Thai' },
] as const;

const BASE_LANG_LABELS: Record<string, string> = Object.fromEntries(
  ALL_BASE_LANGS.map(b => [b.code, b.label])
);

const LEVEL_COLORS: Record<string, string> = {
  A0: 'bg-violet-900/50 text-violet-300',
  A1: 'bg-emerald-900/50 text-emerald-300',
  A2: 'bg-teal-900/50 text-teal-300',
  B1: 'bg-blue-900/50 text-blue-300',
  B2: 'bg-indigo-900/50 text-indigo-300',
  C1: 'bg-amber-900/50 text-amber-300',
  C2: 'bg-rose-900/50 text-rose-300',
};


function formatTs(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${ms}`;
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + '...' : s;
}

function LangSearchSelect({ options, value, onChange, className }: {
  options: { code: string; label: string }[];
  value: string;
  onChange: (code: string) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find(o => o.code === value);

  const filtered = query.trim()
    ? options.filter(o => {
        const q = query.trim().toUpperCase();
        return o.code.toUpperCase().startsWith(q)
          || o.label.toUpperCase().includes(q);
      })
    : options;

  // Sort: code-prefix matches first, then alphabetical
  const sorted = query.trim()
    ? [...filtered].sort((a, b) => {
        const q = query.trim().toUpperCase();
        const aCode = a.code.toUpperCase().startsWith(q) ? 0 : 1;
        const bCode = b.code.toUpperCase().startsWith(q) ? 0 : 1;
        if (aCode !== bCode) return aCode - bCode;
        return a.label.localeCompare(b.label);
      })
    : filtered;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      <input
        ref={inputRef}
        type="text"
        value={open ? query : (selected ? `${selected.label} (${selected.code})` : value)}
        placeholder="Type code or name…"
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
        onKeyDown={e => {
          if (e.key === 'Escape') { setOpen(false); setQuery(''); inputRef.current?.blur(); }
          if (e.key === 'Enter' && sorted.length > 0 && sorted[0]) {
            onChange(sorted[0].code);
            setOpen(false);
            setQuery('');
            inputRef.current?.blur();
          }
        }}
        className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-full cursor-text"
      />
      {open && (
        <div className="absolute z-50 mt-1 w-72 max-h-60 overflow-y-auto bg-zinc-800 border border-zinc-600 rounded shadow-lg">
          {sorted.length === 0 && (
            <div className="px-3 py-2 text-xs text-zinc-500">No match</div>
          )}
          {sorted.slice(0, 50).map(o => (
            <button key={o.code} type="button"
              onClick={() => { onChange(o.code); setOpen(false); setQuery(''); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-700 ${o.code === value ? 'bg-zinc-700/60 text-zinc-100' : 'text-zinc-300'}`}>
              <span className="font-mono text-zinc-400 mr-1.5">{o.code}</span>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function safeRating(r: unknown): RatingScores | null {
  if (!r || typeof r !== 'object') return null;
  const raw = r as Record<string, unknown>;
  const num = (v: unknown): number | undefined => {
    if (typeof v === 'number') return v;
    if (typeof v === 'object' && v !== null && 'score' in v) return (v as { score: number }).score;
    return undefined;
  };
  const overall = num(raw['overall']);
  if (overall === undefined) return null;
  return { overall, accuracy: num(raw['accuracy']), coverage: num(raw['coverage']), quantity: num(raw['quantity']), transcription: num(raw['transcription']) };
}

export default function CefrDocs({ level }: CefrDocsProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCell, setSelectedCell] = useState<{ lang: string; level: string; baseLang?: string } | null>(null);
  const [selectedBaseLang, setSelectedBaseLang] = useState<string | null>(null);
  const [matrixBaseLang, setMatrixBaseLang] = useState<string | null>(null);
  const [showNewLangDialog, setShowNewLangDialog] = useState(false);
  const [newLangCode, setNewLangCode] = useState('');
  const [newLangName, setNewLangName] = useState('');
  const [newLangLevel, setNewLangLevel] = useState('A1');
  const [newLangBase, setNewLangBase] = useState('en');
  const [docContent, setDocContent] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [rating, setRating] = useState(false);
  const [improving, setImproving] = useState(false);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [qaing, setQaing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [improveInstruction, setImproveInstruction] = useState('');
  const [showFixModal, setShowFixModal] = useState(false);
  const [fixSection, setFixSection] = useState('');
  const [fixIssues, setFixIssues] = useState<string[]>([]);
  const [fixType, setFixType] = useState('');
  const [fixInstructions, setFixInstructions] = useState('');
  const [fixPreflight, setFixPreflight] = useState<ValidationResult | null>(null);
  const [fixMode, setFixMode] = useState<'fix' | 'qa'>('fix');
  const [preflight, setPreflight] = useState<ValidationResult | null>(null);
  const [currentRating, setCurrentRating] = useState<FullRating | null>(null);
  const [generatedDocs, setGeneratedDocs] = useState<Map<string, GeneratedDoc>>(new Map());
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [consoleOpen, setConsoleOpen] = useState(true);
  const [expandedLog, setExpandedLog] = useState<number | 'all' | null>(null);
  const [activeTab, setActiveTab] = useState<'documents' | 'fixqa' | 'localization' | 'generate'>('documents');
  const [fixQaHistory, setFixQaHistory] = useState<FixQaRunStatus[]>([]);
  const [fixQaPolling, setFixQaPolling] = useState(false);
  const [fixQaLang, setFixQaLang] = useState('VIE');
  const [fixQaLevel, setFixQaLevel] = useState('A1');
  const [fixQaSection, setFixQaSection] = useState('');
  const [fixQaStarting, setFixQaStarting] = useState(false);
  const [fixQaExpandedRun, setFixQaExpandedRun] = useState<string | null>(null);
  const [fixQaQueue, setFixQaQueue] = useState<Array<{language_code: string; level: string; status: string}>>([]);
  const [fixQaQueueLangs, setFixQaQueueLangs] = useState<string[]>([]);
  const [fixQaSchedule, setFixQaSchedule] = useState<Array<{level: string; language_code: string; date: string; time: string}>>([]);
  const [fixQaScheduleSubmitting, setFixQaScheduleSubmitting] = useState(false);

  const [locHistory, setLocHistory] = useState<LocRunStatus[]>([]);
  const [locPolling, setLocPolling] = useState(false);
  const [locLang, setLocLang] = useState('ENG_GB');
  const [locLevel, setLocLevel] = useState('A1');
  const [locTargetBase, setLocTargetBase] = useState('zh-TW');
  const [locSourceBase, setLocSourceBase] = useState('en');
  const [locStarting, setLocStarting] = useState(false);
  const [locExpandedRun, setLocExpandedRun] = useState<string | null>(null);
  const [locExpandedSteps, setLocExpandedSteps] = useState<Record<string, LocRunStatus['steps']>>({});
  const [locQueue, setLocQueue] = useState<Array<{language_code: string; level: string; target_base_lang: string; source_base_lang: string; status: string}>>([]);
  const [locSchedule, setLocSchedule] = useState<Array<{level: string; language_code: string; target_base_lang: string; source_base_lang: string; date: string; time: string}>>([]);
  const [locScheduleSubmitting, setLocScheduleSubmitting] = useState(false);

  // Generate tab state
  interface TierLanguage { language_code: string; display_name: string; tier: number; }
  const [genHistory, setGenHistory] = useState<FixQaRunStatus[]>([]);
  const [genPolling, setGenPolling] = useState(false);
  const [genQueue, setGenQueue] = useState<Array<{language_code: string; level: string; language_name: string; status: string}>>([]);
  const [genSchedule, setGenSchedule] = useState<Array<{level: string; language_code: string; language_name: string; base_lang: string; date: string; time: string; custom_instructions: string}>>([]);
  const [genScheduleSubmitting, setGenScheduleSubmitting] = useState(false);
  const [genExpandedRun, setGenExpandedRun] = useState<string | null>(null);
  const [tierLangs, setTierLangs] = useState<TierLanguage[]>([]);
  const [tiersLoaded, setTiersLoaded] = useState(false);

  const logContainerRef = useRef<HTMLDivElement>(null);
  const logIdRef = useRef(0);

  const addLog = useCallback((entry: Omit<LogEntry, 'id' | 'ts'>) => {
    const newEntry: LogEntry = { ...entry, id: ++logIdRef.current, ts: formatTs() };
    setLogs((prev) => [...prev, newEntry]);
    setTimeout(() => {
      const el = logContainerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
    return newEntry.id;
  }, []);

  const apiCall = useCallback(async (
    method: string,
    path: string,
    body?: Record<string, unknown>,
  ): Promise<{ ok: boolean; status: number; data: unknown; durationMs: number }> => {
    const url = `${BASE_URL}${path}`;
    const bodyStr = body ? JSON.stringify(body, null, 2) : undefined;

    addLog({ type: 'request', method, url, requestBody: bodyStr, message: `${method} ${path}` });

    const token = useAuthStore.getState().token;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const start = performance.now();
    try {
      const res = await fetch(url, { method, headers, body: bodyStr });
      const durationMs = Math.round(performance.now() - start);
      let data: unknown;
      const text = await res.text();
      try { data = JSON.parse(text); } catch { data = text; }

      addLog({
        type: res.ok ? 'response' : 'error',
        method, url, status: res.status, durationMs,
        responseBody: typeof data === 'string' ? data : JSON.stringify(data, null, 2),
        message: `${res.status} ${res.statusText} (${durationMs}ms)`,
      });

      return { ok: res.ok, status: res.status, data, durationMs };
    } catch (err) {
      const durationMs = Math.round(performance.now() - start);
      const msg = err instanceof Error ? err.message : String(err);
      addLog({ type: 'error', method, url, durationMs, message: `Network error: ${msg} (${durationMs}ms)` });
      return { ok: false, status: 0, data: msg, durationMs };
    }
  }, [addLog]);

  // Load docs from database on mount
  useEffect(() => {
    addLog({ type: 'info', message: 'Loading CEFR docs from database...' });

    fetch(`${BASE_URL}/api/admin/docs?_t=${Date.now()}`)
      .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json() as Promise<{ docs: GeneratedDoc[] }>; })
      .then(({ docs }: { docs: (GeneratedDoc & { rating?: unknown })[] }) => {
        const map = new Map<string, GeneratedDoc>();
        for (const d of docs) {
          const doc = { ...d, rating: safeRating(d.rating) };
          map.set(d.key, doc);
          // Also index under baseLang-suffixed key so getCellState finds docs
          // where display base_lang differs from the storage key format
          // (e.g. key=A0_ENG_US but base_lang=ja → also index as A0_ENG_US__ja)
          const bl = d.base_lang || 'en';
          const suffixedKey = bl !== 'en' ? `${d.level}_${d.language_code}__${bl}` : `${d.level}_${d.language_code}`;
          if (suffixedKey !== d.key) map.set(suffixedKey, doc);
        }
        addLog({ type: 'response', message: `Docs loaded: ${docs.length} found in DynamoDB` });
        setGeneratedDocs(map);
      })
      .catch((err) => {
        addLog({ type: 'error', message: `Docs fetch failed: ${err instanceof Error ? err.message : 'unknown'}` });
        setError(err instanceof Error ? err.message : 'Failed to load');
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowFixModal(false); };
    if (showFixModal) { document.addEventListener('keydown', handler); return () => document.removeEventListener('keydown', handler); }
  }, [showFixModal]);

  // FIX_QA polling — refresh full history every 5s while any run is active or scheduled
  useEffect(() => {
    if (activeTab !== 'fixqa') return;
    const refreshHistory = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/docs/fixqa/history`);
        if (res.ok) {
          const data = await res.json() as { runs: FixQaRunStatus[] };
          setFixQaHistory(data.runs || []);
          const hasActive = (data.runs || []).some((r: FixQaRunStatus) => r.status === 'running' || r.status === 'scheduled');
          setFixQaPolling(hasActive);
        }
      } catch { /* ignore */ }
    };
    refreshHistory();
    const interval = setInterval(refreshHistory, 5000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Localization polling — refresh history every 5s while any run is active
  useEffect(() => {
    if (activeTab !== 'localization') return;
    const refreshLocHistory = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/docs/localize/history`);
        if (res.ok) {
          const data = await res.json() as { runs: LocRunStatus[] };
          setLocHistory(data.runs || []);
          const hasActive = (data.runs || []).some((r: LocRunStatus) => r.status === 'running' || r.status === 'scheduled');
          setLocPolling(hasActive);
        }
      } catch { /* ignore */ }
      try {
        const qRes = await fetch(`${BASE_URL}/api/admin/docs/localize/queue`);
        if (qRes.ok) setLocQueue((await qRes.json()).items || []);
      } catch { /* ignore */ }
    };
    refreshLocHistory();
    const interval = setInterval(refreshLocHistory, 5000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Generate tab polling
  useEffect(() => {
    if (activeTab !== 'generate') return;
    const refreshGenHistory = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/docs/generate/history`);
        if (res.ok) {
          const data = await res.json() as { runs: FixQaRunStatus[] };
          setGenHistory(data.runs || []);
          const hasActive = (data.runs || []).some((r: FixQaRunStatus) => r.status === 'running');
          setGenPolling(hasActive);
        }
      } catch { /* ignore */ }
      try {
        const qRes = await fetch(`${BASE_URL}/api/admin/docs/generate/queue`);
        if (qRes.ok) setGenQueue((await qRes.json()).items || []);
      } catch { /* ignore */ }
    };
    refreshGenHistory();
    const interval = setInterval(refreshGenHistory, 5000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Fetch tiers when generate tab is active
  useEffect(() => {
    if (activeTab !== 'generate' || tiersLoaded) return;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/languages/tiers`);
        if (res.ok) {
          const data = await res.json() as { languages: Array<{ language_code: string; display_name?: string; name_english?: string; tier: number }> };
          setTierLangs((data.languages || []).map(l => ({
            language_code: l.language_code,
            display_name: l.display_name || l.name_english || l.language_code,
            tier: l.tier,
          })));
          setTiersLoaded(true);
        }
      } catch { /* ignore */ }
    })();
  }, [activeTab, tiersLoaded]);

  const handleFixQaStart = async () => {
    setFixQaStarting(true);
    try {
      const fixBaseLang = selectedCell?.baseLang && selectedCell.baseLang !== 'en' ? selectedCell.baseLang : undefined;
      const res = await apiCall('POST', '/api/admin/docs/fixqa/start', {
        language_code: fixQaLang,
        level: fixQaLevel,
        created_by: 'michael@glossika.com',
        ...(fixQaSection ? { only_section: fixQaSection } : {}),
        ...(fixBaseLang ? { base_lang: fixBaseLang } : {}),
      });
      if (res.ok) {
        addLog({ type: 'info', message: `FIX_QA started for ${fixQaLang} ${fixQaLevel}${fixQaSection ? ` (§${fixQaSection} only)` : ''}` });
        setFixQaPolling(true);
      } else {
        const err = res.data as { error?: string };
        addLog({ type: 'error', message: `FIX_QA start failed: ${err?.error || 'Unknown error'}` });
      }
    } finally {
      setFixQaStarting(false);
    }
  };

  const handleFixQaCancel = async (docKey: string) => {
    const res = await apiCall('POST', '/api/admin/docs/fixqa/cancel', { doc_key: docKey });
    if (res.ok) {
      addLog({ type: 'info', message: `FIX_QA cancelled: ${docKey}` });
      setFixQaHistory(prev => prev.map(r => r.doc_key === docKey ? { ...r, status: 'cancelled' as const } : r));
    }
  };

  const handleFixQaRestart = async (docKey: string, startStep: number, label: string) => {
    const run = fixQaHistory.find(r => r.doc_key === docKey);
    if (!run) return;
    // Cancel first if still active
    if (run.status === 'running' || run.status === 'scheduled') {
      await apiCall('POST', '/api/admin/docs/fixqa/cancel', { doc_key: docKey });
      // Brief wait for cancellation to propagate
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    const res = await apiCall('POST', '/api/admin/docs/fixqa/start', {
      language_code: run.language_code,
      level: run.level,
      created_by: 'michael@glossika.com',
      start_step: startStep,
    });
    if (res.ok) {
      addLog({ type: 'info', message: `FIX_QA restarted ${docKey} from ${label}` });
    } else {
      const err = res.data as { error?: string };
      addLog({ type: 'error', message: `Restart failed: ${err?.error || 'Unknown error'}` });
    }
  };

  const [fixQaExpandedSteps, setFixQaExpandedSteps] = useState<Record<string, FixQaRunStatus['steps']>>({});

  const handleFixQaViewRun = async (docKey: string) => {
    if (fixQaExpandedRun === docKey) { setFixQaExpandedRun(null); return; }
    setFixQaExpandedRun(docKey);
    try {
      const res = await fetch(`${BASE_URL}/api/admin/docs/fixqa/status?doc_key=${encodeURIComponent(docKey)}`);
      if (res.ok) {
        const data = await res.json() as FixQaRunStatus;
        setFixQaExpandedSteps(prev => ({ ...prev, [docKey]: data.steps }));
      }
    } catch { /* ignore */ }
  };

  const handleFixQaQueueSubmit = async () => {
    if (fixQaQueueLangs.length === 0) return;
    const items = fixQaQueueLangs.map(lang => ({ language_code: lang, level: fixQaLevel }));
    try {
      const res = await apiCall('POST', '/api/admin/docs/fixqa/queue', { items });
      if (res.ok) {
        addLog({ type: 'info', message: `Queued ${items.length} documents for FIX_QA` });
        const data = res.data as { queued: number; started: boolean };
        if (data.started) {
          setFixQaPolling(true);
          setFixQaLang(items[0]!.language_code);
        }
        // Refresh queue
        const qRes = await fetch(`${BASE_URL}/api/admin/docs/fixqa/queue`);
        if (qRes.ok) setFixQaQueue((await qRes.json()).items || []);
        setFixQaQueueLangs([]);
      }
    } catch { addLog({ type: 'error', message: 'Failed to submit queue' }); }
  };

  const handleFixQaQueueClear = async () => {
    await apiCall('DELETE', '/api/admin/docs/fixqa/queue', {});
    setFixQaQueue([]);
    addLog({ type: 'info', message: 'Queue cleared' });
  };

  const handleFixQaScheduleAddRow = () => {
    const last = fixQaSchedule[fixQaSchedule.length - 1];
    let nextDate = '';
    let nextTime = '';
    if (last) {
      const d = new Date(`${last.date}T${last.time}:00+08:00`);
      d.setMinutes(d.getMinutes() + 30);
      const utc8 = new Date(d.getTime() + 8 * 60 * 60 * 1000);
      nextDate = `${utc8.getUTCFullYear()}-${String(utc8.getUTCMonth() + 1).padStart(2, '0')}-${String(utc8.getUTCDate()).padStart(2, '0')}`;
      nextTime = `${String(utc8.getUTCHours()).padStart(2, '0')}:${String(utc8.getUTCMinutes()).padStart(2, '0')}`;
    } else {
      const now = new Date(Date.now() + 8 * 60 * 60 * 1000);
      const mins = now.getUTCMinutes();
      if (mins < 30) { now.setUTCMinutes(30, 0, 0); }
      else { now.setUTCHours(now.getUTCHours() + 1, 0, 0, 0); }
      nextDate = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
      nextTime = `${String(now.getUTCHours()).padStart(2, '0')}:${String(now.getUTCMinutes()).padStart(2, '0')}`;
    }
    setFixQaSchedule(prev => [...prev, { level: 'C2', language_code: allLangs[0] || 'VIE', date: nextDate, time: nextTime }]);
  };

  const handleFixQaScheduleRemoveRow = (idx: number) => {
    setFixQaSchedule(prev => prev.filter((_, i) => i !== idx));
  };

  const handleFixQaScheduleUpdate = (idx: number, field: string, value: string) => {
    setFixQaSchedule(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };

  const handleFixQaScheduleSubmit = async () => {
    if (fixQaSchedule.length === 0) return;
    setFixQaScheduleSubmitting(true);
    let submitted = 0;
    try {
      for (const row of fixQaSchedule) {
        const utcIso = new Date(`${row.date}T${row.time}:00+08:00`).toISOString();
        const res = await apiCall('POST', '/api/admin/docs/fixqa/start', {
          language_code: row.language_code,
          level: row.level,
          created_by: 'michael@glossika.com',
          scheduled_at: utcIso,
        });
        if (res.ok) submitted++;
        else addLog({ type: 'error', message: `Failed to schedule ${row.level} ${row.language_code}` });
      }
      addLog({ type: 'info', message: `Scheduled ${submitted}/${fixQaSchedule.length} runs` });
      if (submitted > 0) {
        setFixQaSchedule([]);
        // Refresh history
        try {
          const hRes = await fetch(`${BASE_URL}/api/admin/docs/fixqa/history`);
          if (hRes.ok) setFixQaHistory((await hRes.json()).runs || []);
        } catch { /* ignore */ }
      }
    } catch { addLog({ type: 'error', message: 'Schedule submission failed' }); }
    setFixQaScheduleSubmitting(false);
  };

  // --- Localization handlers ---
  const handleLocStart = async () => {
    setLocStarting(true);
    try {
      const res = await apiCall('POST', '/api/admin/docs/localize/start', {
        language_code: locLang,
        level: locLevel,
        target_base_lang: locTargetBase,
        source_base_lang: locSourceBase,
        created_by: 'michael@glossika.com',
      });
      if (res.ok) {
        addLog({ type: 'info', message: `Localization started: ${locLang} ${locLevel} → ${locTargetBase}` });
        setLocPolling(true);
      } else {
        const err = res.data as { error?: string };
        addLog({ type: 'error', message: `Localization start failed: ${err?.error || 'Unknown error'}` });
      }
    } finally {
      setLocStarting(false);
    }
  };

  const handleLocCancel = async (docKey: string) => {
    const res = await apiCall('POST', '/api/admin/docs/localize/cancel', { doc_key: docKey });
    if (res.ok) {
      addLog({ type: 'info', message: `Localization cancelled: ${docKey}` });
      setLocHistory(prev => prev.map(r => r.doc_key === docKey ? { ...r, status: 'cancelled' as const } : r));
    }
  };

  const handleLocRestart = async (docKey: string, startStep: number, label: string) => {
    const run = locHistory.find(r => r.doc_key === docKey);
    if (!run) return;
    if (run.status === 'running' || run.status === 'scheduled') {
      await apiCall('POST', '/api/admin/docs/localize/cancel', { doc_key: docKey });
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    const res = await apiCall('POST', '/api/admin/docs/localize/start', {
      language_code: run.language_code,
      level: run.level,
      target_base_lang: run.target_base_lang,
      source_base_lang: run.source_base_lang,
      created_by: 'michael@glossika.com',
      start_step: startStep,
    });
    if (res.ok) {
      addLog({ type: 'info', message: `Localization restarted ${docKey} from ${label}` });
    } else {
      const err = res.data as { error?: string };
      addLog({ type: 'error', message: `Restart failed: ${err?.error || 'Unknown error'}` });
    }
  };

  const handleLocViewRun = async (docKey: string) => {
    if (locExpandedRun === docKey) { setLocExpandedRun(null); return; }
    setLocExpandedRun(docKey);
    try {
      const res = await fetch(`${BASE_URL}/api/admin/docs/localize/status?doc_key=${encodeURIComponent(docKey)}`);
      if (res.ok) {
        const data = await res.json() as LocRunStatus;
        setLocExpandedSteps(prev => ({ ...prev, [docKey]: data.steps }));
      }
    } catch { /* ignore */ }
  };

  const handleLocQueueSubmit = async () => {
    const items = locSchedule.map(row => ({
      language_code: row.language_code,
      level: row.level,
      target_base_lang: row.target_base_lang,
      source_base_lang: row.source_base_lang,
    }));
    if (items.length === 0) return;
    try {
      const res = await apiCall('POST', '/api/admin/docs/localize/queue', { items });
      if (res.ok) {
        addLog({ type: 'info', message: `Queued ${items.length} localization jobs` });
        setLocSchedule([]);
        setLocPolling(true);
        const qRes = await fetch(`${BASE_URL}/api/admin/docs/localize/queue`);
        if (qRes.ok) setLocQueue((await qRes.json()).items || []);
      }
    } catch { addLog({ type: 'error', message: 'Failed to submit localization queue' }); }
  };

  const handleLocQueueClear = async () => {
    await apiCall('DELETE', '/api/admin/docs/localize/queue', {});
    setLocQueue([]);
    addLog({ type: 'info', message: 'Localization queue cleared' });
  };

  const handleLocScheduleAddRow = () => {
    setLocSchedule(prev => [...prev, {
      level: locLevel,
      language_code: locLang,
      target_base_lang: locTargetBase,
      source_base_lang: locSourceBase,
      date: '',
      time: '',
    }]);
  };

  const handleLocScheduleRemoveRow = (idx: number) => {
    setLocSchedule(prev => prev.filter((_, i) => i !== idx));
  };

  const handleLocScheduleUpdate = (idx: number, field: string, value: string) => {
    setLocSchedule(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };

  const handleLocScheduleSubmit = async () => {
    if (locSchedule.length === 0) return;
    setLocScheduleSubmitting(true);
    let submitted = 0;
    try {
      for (const row of locSchedule) {
        const utcIso = row.date && row.time ? new Date(`${row.date}T${row.time}:00+08:00`).toISOString() : undefined;
        const res = await apiCall('POST', '/api/admin/docs/localize/start', {
          language_code: row.language_code,
          level: row.level,
          target_base_lang: row.target_base_lang,
          source_base_lang: row.source_base_lang,
          created_by: 'michael@glossika.com',
          ...(utcIso ? { scheduled_at: utcIso } : {}),
        });
        if (res.ok) submitted++;
        else addLog({ type: 'error', message: `Failed to schedule ${row.level} ${row.language_code} → ${row.target_base_lang}` });
      }
      addLog({ type: 'info', message: `Scheduled ${submitted}/${locSchedule.length} localization runs` });
      if (submitted > 0) {
        setLocSchedule([]);
        try {
          const hRes = await fetch(`${BASE_URL}/api/admin/docs/localize/history`);
          if (hRes.ok) setLocHistory((await hRes.json()).runs || []);
        } catch { /* ignore */ }
      }
    } catch { addLog({ type: 'error', message: 'Localization schedule submission failed' }); }
    setLocScheduleSubmitting(false);
  };

  // --- Generate handlers ---
  const handleGenQueueSubmit = async () => {
    if (genSchedule.length === 0) return;
    setGenScheduleSubmitting(true);
    try {
      const items = genSchedule.map(row => {
        const item: { language_code: string; level: string; language_name: string; base_lang: string; scheduled_at?: string; custom_instructions?: string } = {
          language_code: row.language_code,
          level: row.level,
          language_name: row.language_name,
          base_lang: row.base_lang,
        };
        if (row.date && row.time) {
          item.scheduled_at = new Date(`${row.date}T${row.time}:00+08:00`).toISOString();
        }
        if (row.custom_instructions.trim()) {
          item.custom_instructions = row.custom_instructions.trim();
        }
        return item;
      });
      const res = await apiCall('POST', '/api/admin/docs/generate/queue', { items });
      if (res.ok) {
        const hasScheduled = items.some(i => i.scheduled_at);
        addLog({ type: 'info', message: hasScheduled ? `Scheduled ${items.length} documents for generation` : `Queued ${items.length} documents for generation` });
        setGenSchedule([]);
        setGenPolling(true);
        for (const item of items) {
          if (item.language_name) {
            setCustomLangName(item.language_code, item.language_name);
          }
        }
      } else {
        const err = res.data as { error?: string };
        addLog({ type: 'error', message: `Queue failed: ${err?.error || 'Unknown error'}` });
      }
    } catch (err) { addLog({ type: 'error', message: `Generate queue submission failed: ${err instanceof Error ? err.message : String(err)}` }); }
    setGenScheduleSubmitting(false);
  };

  const handleGenQueueClear = async () => {
    await apiCall('DELETE', '/api/admin/docs/generate/queue', {});
    setGenQueue([]);
    addLog({ type: 'info', message: 'Generate queue cleared' });
  };

  const handleGenCancel = async (docKey: string) => {
    const res = await apiCall('POST', '/api/admin/docs/generate/cancel', { doc_key: docKey });
    if (res.ok) {
      addLog({ type: 'info', message: `Generation cancelled: ${docKey}` });
      setGenHistory(prev => prev.map(r => r.doc_key === docKey ? { ...r, status: 'cancelled' as const } : r));
    }
  };

  const handleGenScheduleAddRow = () => {
    const firstTier = tierLangs.length > 0 ? tierLangs[0] : null;
    const last = genSchedule[genSchedule.length - 1];
    let nextDate = '';
    let nextTime = '';
    if (last && last.date && last.time) {
      const d = new Date(`${last.date}T${last.time}:00+08:00`);
      d.setMinutes(d.getMinutes() + 30);
      const utc8 = new Date(d.getTime() + 8 * 60 * 60 * 1000);
      nextDate = `${utc8.getUTCFullYear()}-${String(utc8.getUTCMonth() + 1).padStart(2, '0')}-${String(utc8.getUTCDate()).padStart(2, '0')}`;
      nextTime = `${String(utc8.getUTCHours()).padStart(2, '0')}:${String(utc8.getUTCMinutes()).padStart(2, '0')}`;
    } else {
      const now = new Date(Date.now() + 8 * 60 * 60 * 1000);
      const mins = now.getUTCMinutes();
      if (mins < 30) { now.setUTCMinutes(30, 0, 0); }
      else { now.setUTCHours(now.getUTCHours() + 1, 0, 0, 0); }
      nextDate = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
      nextTime = `${String(now.getUTCHours()).padStart(2, '0')}:${String(now.getUTCMinutes()).padStart(2, '0')}`;
    }
    setGenSchedule(prev => [...prev, {
      level: 'A0',
      language_code: firstTier?.language_code || '',
      language_name: firstTier?.display_name || '',
      base_lang: last?.base_lang || 'en',
      date: nextDate,
      time: nextTime,
      custom_instructions: '',
    }]);
  };

  const handleGenScheduleRemoveRow = (idx: number) => {
    setGenSchedule(prev => prev.filter((_, i) => i !== idx));
  };

  const handleGenScheduleUpdate = (idx: number, field: string, value: string) => {
    setGenSchedule(prev => prev.map((row, i) => {
      if (i !== idx) return row;
      if (field === 'language_code') {
        const lang = tierLangs.find(t => t.language_code === value);
        return { ...row, language_code: value, language_name: lang?.display_name || value };
      }
      return { ...row, [field]: value };
    }));
  };

  const getTierForLang = (code: string): number => {
    const t = tierLangs.find(l => l.language_code === code);
    return t?.tier || 0;
  };

  const getModelForTier = (tier: number): string => {
    return tier <= 2 ? 'claude-opus-4-6' : 'claude-opus-4-7';
  };

  // Only show languages that exist in the database
  const allLangSet = new Set<string>();
  const langBaseLangs = new Map<string, Set<string>>();
  for (const [, doc] of generatedDocs) {
    allLangSet.add(doc.language_code);
    const bl = doc.base_lang || 'en';
    if (!langBaseLangs.has(doc.language_code)) langBaseLangs.set(doc.language_code, new Set());
    langBaseLangs.get(doc.language_code)!.add(bl);
  }
  const allLangs = [...allLangSet].sort((a, b) => langName(a).localeCompare(langName(b)));

  const getCellState = (lang: string, lvl: string, baseLang?: string): CellState => {
    if (baseLang && baseLang !== 'en') {
      const key = `${lvl}_${lang}__${baseLang}`;
      const gen = generatedDocs.get(key);
      if (gen) return { status: gen.status === 'approved' ? 'approved' : 'draft', source: 'db' };
      return { status: 'none', source: null };
    }
    const key = `${lvl}_${lang}`;
    const gen = generatedDocs.get(key);
    if (gen) return { status: gen.status === 'approved' ? 'approved' : 'draft', source: 'db' };
    return { status: 'none', source: null };
  };

  const loadDoc = useCallback((lang: string, lvl: string, baseLang?: string) => {
    const bl = baseLang || 'en';
    const cell = getCellState(lang, lvl, bl);
    setDocLoading(true);
    setDocContent(null);

    if (cell.source === 'db') {
      // Use the actual storage key from the map — NOT a constructed key.
      // The map stores docs under both the raw API key and the baseLang-suffixed key.
      // The raw API key matches the DynamoDB vertical_id structure.
      const lookupKey = bl === 'en' ? `${lvl}_${lang}` : `${lvl}_${lang}__${bl}`;
      const mapEntry = generatedDocs.get(lookupKey);
      const key = mapEntry?.key || lookupKey;
      addLog({ type: 'info', message: `Loading generated doc from API: ${key}` });
      const start = performance.now();
      fetch(`${BASE_URL}/api/admin/docs/${encodeURIComponent(key)}?_t=${Date.now()}`)
        .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json() as Promise<{ content: string }>; })
        .then(({ content }) => {
          const dur = Math.round(performance.now() - start);
          addLog({ type: 'response', durationMs: dur, message: `Doc loaded from DB: ${key} (${content.length} chars, ${dur}ms)` });
          setDocContent(content);
        })
        .catch((err) => {
          const dur = Math.round(performance.now() - start);
          addLog({ type: 'error', durationMs: dur, message: `Doc load failed: ${err instanceof Error ? err.message : 'unknown'}` });
          setDocContent(`Error loading: ${err instanceof Error ? err.message : 'unknown'}`);
        })
        .finally(() => setDocLoading(false));
    } else {
      setDocLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedDocs, addLog]);

  const fetchDocContent = useCallback(async (lang: string, lvl: string): Promise<string | null> => {
    const key = `${lvl}_${lang}`;
    const cell = getCellState(lang, lvl);
    if (cell.source === 'db') {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/docs/${encodeURIComponent(key)}?_t=${Date.now()}`);
        if (!res.ok) return null;
        const data = await res.json() as { content?: string };
        return data.content ?? null;
      } catch { return null; }
    }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedDocs]);

  // Resolve the actual DynamoDB storage key for a cell, honoring its base language and the doc
  // map (which indexes docs under both raw and baseLang-suffixed keys). Every handler that
  // reads/mutates a specific cell must use this so operations land on the correct per-language
  // doc — a bare `${lvl}_${lang}` key silently targets the no-suffix base doc.
  const resolveDocKey = useCallback((lvl: string, lang: string, bl: string): string => {
    const lookupKey = bl && bl !== 'en' ? `${lvl}_${lang}__${bl}` : `${lvl}_${lang}`;
    return generatedDocs.get(lookupKey)?.key || lookupKey;
  }, [generatedDocs]);

  const getEffectiveBaseLang = useCallback((lang: string): string => {
    if (selectedBaseLang) return selectedBaseLang;
    return getBaseLangs(lang)[0] ?? 'en';
  }, [selectedBaseLang]);

  const handleCellClick = (lang: string, lvl: string, baseLang?: string) => {
    const bl = baseLang || 'en';
    const cell = getCellState(lang, lvl, bl);
    setSelectedCell({ lang, level: lvl, baseLang: bl });
    setSelectedBaseLang(bl === 'en' ? null : bl);
    setCurrentRating(null);
    setPreflight(null);
    addLog({ type: 'info', message: `Selected: ${langName(lang)} (${lang}) ${lvl} [${bl}] — status: ${cell.status}` });
    if (cell.status !== 'none') loadDoc(lang, lvl, bl);
    else setDocContent(null);
  };

  const handleGenerate = async (regenerate = false) => {
    if (!selectedCell) return;
    setGenerating(true);
    setDocContent(null);
    setCurrentRating(null);
    setPreflight(null);
    const { lang, level: lvl } = selectedCell;
    const baseLang = getEffectiveBaseLang(lang);
    const payload: Record<string, unknown> = {
      language_code: lang,
      level: lvl,
      language_name: langName(lang),
      base_lang: baseLang,
      doc_type: lvl === 'A0' ? 'phonics' : 'cefr',
    };
    if (regenerate) payload.regenerate = true;
    const stageCount = lvl === 'A0' ? 1 : 9;
    addLog({ type: 'info', message: `${regenerate ? 'Re-generating' : 'Generating'}: ${langName(lang)} ${lvl} (base: ${BASE_LANG_LABELS[baseLang] ?? baseLang}) — ${stageCount} API calls (§1-§5, §6-§8, §9 topics ×5, auto-rate)...` });

    const generateStartedAt = new Date().toISOString();
    const result = await apiCall('POST', '/api/admin/docs/generate', payload);

    if (result.ok) {
      const resp = result.data as { content?: string; key?: string; status?: string; rating?: FullRating; stages?: number; usage?: { input_tokens?: number; output_tokens?: number }; message?: string };
      if (resp.content) {
        setDocContent(resp.content);
        const key = `${lvl}_${lang}`;
        const rScores: RatingScores | undefined = resp.rating ? { overall: resp.rating.overall, accuracy: resp.rating.accuracy?.score, coverage: resp.rating.coverage?.score, quantity: resp.rating.quantity?.score, transcription: resp.rating.transcription?.score } : undefined;
        setGeneratedDocs((prev) => {
          const next = new Map(prev);
          next.set(key, { key, language_code: lang, level: lvl, status: (resp.status as 'draft' | 'approved') || 'draft', rating: rScores });
          return next;
        });
        if (resp.rating) {
          applyRating(key, lang, lvl, resp.rating);
          addLog({ type: 'info', message: `Generated + rated: ${resp.rating.overall}/10 (acc=${resp.rating.accuracy?.score} cov=${resp.rating.coverage?.score} qty=${resp.rating.quantity?.score} trn=${resp.rating.transcription?.score}) — ${resp.stages || '?'} stages, ${resp.usage?.output_tokens || '?'} output tokens` });
        } else {
          addLog({ type: 'info', message: `Generated ${langName(lang)} ${lvl}: ${resp.content.length} chars, ${resp.stages || '?'} stages` });
        }
      }
      if (resp.message) addLog({ type: 'info', message: resp.message });
    } else if (result.status === 503 || result.status === 504) {
      addLog({ type: 'info', message: `API Gateway timeout — Lambda continues: staged generation (8-12 min for A1-C2)...` });
      let attempts = 0;
      let lastStage = 0;
      const poll = async () => {
        attempts++;
        const key = `${lvl}_${lang}`;
        const check = await apiCall('GET', `/api/admin/docs/${encodeURIComponent(key)}`);
        if (check.ok) {
          const data = check.data as { content?: string; status?: string; rating?: FullRating; updated_at?: string; generate_stage?: string | number };
          const stage = Number(data.generate_stage) || 0;
          if (stage > lastStage) {
            const stageNames: Record<number, string> = { 1: '§1–§5 functional', 2: '§6–§8 discourse/culture', 3: '§9.1–§9.5', 4: '§9.6–§9.10', 5: '§9.11–§9.15', 6: '§9.16–§9.20', 7: '§9.21–§9.25' };
            addLog({ type: 'info', message: `Stage ${stage} complete: ${stageNames[stage] || `stage ${stage}`}` });
            lastStage = stage;
          }
          if (data.updated_at && data.updated_at >= generateStartedAt && data.generate_stage === 'complete') {
            if (data.content) setDocContent(data.content);
            const rScores: RatingScores | undefined = data.rating ? { overall: data.rating.overall, accuracy: data.rating.accuracy?.score, coverage: data.rating.coverage?.score, quantity: data.rating.quantity?.score, transcription: data.rating.transcription?.score } : undefined;
            setGeneratedDocs((prev) => {
              const next = new Map(prev);
              next.set(key, { key, language_code: lang, level: lvl, status: (data.status as 'draft' | 'approved') || 'draft', rating: rScores });
              return next;
            });
            if (data.rating) {
              applyRating(key, lang, lvl, data.rating);
              const r = data.rating;
              addLog({ type: 'info', message: `Generate + rate complete: ${r.overall}/10 (acc=${r.accuracy?.score} cov=${r.coverage?.score} qty=${r.quantity?.score} trn=${r.transcription?.score})` });
            } else {
              addLog({ type: 'info', message: `Generate complete (rating not available)` });
            }
            setGenerating(false);
            return;
          }
        }
        if (attempts < 168) {
          const elapsed = attempts * 5;
          addLog({ type: 'info', message: `Poll ${attempts}/168 (${elapsed}s) — generating...` });
          setTimeout(poll, 5000);
        } else {
          addLog({ type: 'error', message: 'Generation timed out after 14 minutes of polling. Check Lambda logs.' });
          setGenerating(false);
        }
      };
      setTimeout(poll, 5000);
      return;
    }
    setGenerating(false);
  };

  const handleApprove = async () => {
    if (!selectedCell) return;
    const { lang, level: lvl, baseLang: cellBaseLang } = selectedCell;
    const bl = cellBaseLang || 'en';
    const lookupKey = bl !== 'en' ? `${lvl}_${lang}__${bl}` : `${lvl}_${lang}`;
    const mapEntry = generatedDocs.get(lookupKey);
    const key = mapEntry?.key || lookupKey;
    const currentStatus = getCellState(lang, lvl, bl).status;
    const newStatus = currentStatus === 'approved' ? 'draft' : 'approved';
    const endpoint = newStatus === 'approved' ? 'approve' : 'unapprove';
    addLog({ type: 'info', message: `${newStatus === 'approved' ? 'Approving' : 'Reverting to draft'}: ${langName(lang)} ${lvl} [${bl}]` });
    const result = await apiCall('PUT', `/api/admin/docs/${encodeURIComponent(key)}/${endpoint}`);
    if (result.ok) {
      setGeneratedDocs((prev) => {
        const next = new Map(prev);
        const existing = next.get(key);
        if (existing) next.set(key, { ...existing, status: newStatus });
        const altKey = key !== lookupKey ? lookupKey : null;
        if (altKey) { const alt = next.get(altKey); if (alt) next.set(altKey, { ...alt, status: newStatus }); }
        return next;
      });
      addLog({ type: 'info', message: `${newStatus === 'approved' ? 'Approved' : 'Reverted to draft'}: ${key}` });
    }
  };

  const handleDiscard = async () => {
    if (!selectedCell) return;
    const { lang, level: lvl, baseLang: cellBaseLang } = selectedCell;
    const bl = cellBaseLang || 'en';
    const lookupKey = bl !== 'en' ? `${lvl}_${lang}__${bl}` : `${lvl}_${lang}`;
    const mapEntry = generatedDocs.get(lookupKey);
    const key = mapEntry?.key || lookupKey;
    addLog({ type: 'info', message: `Discarding: ${langName(lang)} ${lvl} [${bl}]` });
    const result = await apiCall('DELETE', `/api/admin/docs/${encodeURIComponent(key)}`);
    if (result.ok) {
      setGeneratedDocs((prev) => { const next = new Map(prev); next.delete(key); next.delete(lookupKey); return next; });
      addLog({ type: 'info', message: `Discarded: ${key}` });
    }
    setSelectedCell(null);
    setDocContent(null);
    setCurrentRating(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedCell) return;
    // Build the storage key from the SELECTED CELL's base language. This previously used
    // `${level}_${lang}` and ignored baseLang, so every manual edit (Korean, French, …) was
    // saved to the no-suffix base key — silently overwriting the base/ja document. Mirror the
    // resolution used by handleApprove/handleDiscard so edits land in the correct per-language doc.
    const { lang, level: lvl, baseLang: cellBaseLang } = selectedCell;
    const bl = cellBaseLang || 'en';
    const lookupKey = bl !== 'en' ? `${lvl}_${lang}__${bl}` : `${lvl}_${lang}`;
    const mapEntry = generatedDocs.get(lookupKey);
    const key = mapEntry?.key || lookupKey;
    addLog({ type: 'info', message: `Saving manual edit: ${key} [${bl}]` });
    const result = await apiCall('PUT', `/api/admin/docs/${encodeURIComponent(key)}`, { content: editContent, ...(bl !== 'en' ? { base_lang: bl } : {}) });
    if (result.ok) {
      setDocContent(editContent);
      addLog({ type: 'info', message: `Saved: ${key}` });
      setGeneratedDocs((prev) => {
        const next = new Map(prev);
        const existing = next.get(key);
        if (existing) next.set(key, { ...existing });
        else next.set(key, { key, language_code: lang, level: lvl, base_lang: bl !== 'en' ? bl : undefined, status: 'draft' });
        if (key !== lookupKey) { const alt = next.get(lookupKey); if (alt) next.set(lookupKey, { ...alt }); }
        return next;
      });
    }
    setEditing(false);
  };

  const handleCancel = async () => {
    if (!selectedCell) return;
    const key = resolveDocKey(selectedCell.level, selectedCell.lang, selectedCell.baseLang || 'en');
    addLog({ type: 'info', message: `Cancelling current job for ${key}...` });
    const result = await apiCall('POST', `/api/admin/docs/${encodeURIComponent(key)}/cancel`, {});
    if (result.ok) {
      addLog({ type: 'info', message: `Cancelled: ${key} — improve/generate stage cleared` });
    }
    setGenerating(false);
    setRating(false);
    setImproving(false);
    setQaing(false);
  };

  const applyRating = useCallback((key: string, lang: string, lvl: string, ratingData: FullRating) => {
    setCurrentRating(ratingData);
    setGeneratedDocs((prev) => {
      const next = new Map(prev);
      const existing = next.get(key);
      const scores: RatingScores = { overall: ratingData.overall, accuracy: ratingData.accuracy.score, coverage: ratingData.coverage.score, quantity: ratingData.quantity.score, transcription: ratingData.transcription.score };
      if (existing) next.set(key, { ...existing, rating: scores });
      else next.set(key, { key, language_code: lang, level: lvl, status: 'draft', rating: scores });
      return next;
    });
    addLog({ type: 'info', message: `Rated ${key}: ${ratingData.overall}/10 (acc=${ratingData.accuracy.score} cov=${ratingData.coverage.score} qty=${ratingData.quantity.score} trn=${ratingData.transcription.score})` });
    if (ratingData.summary) addLog({ type: 'info', message: `Summary: ${ratingData.summary}` });
    for (const criterion of ['accuracy', 'coverage', 'quantity', 'transcription'] as const) {
      const c = ratingData[criterion];
      if (c.issues.length > 0) {
        for (const issue of c.issues) addLog({ type: 'info', message: `[${criterion}] ${issue}` });
      }
    }
  }, [addLog]);

  const handleRate = async () => {
    if (!selectedCell) return;
    setRating(true);
    setCurrentRating(null);
    const { lang, level: lvl } = selectedCell;
    const baseLang = getEffectiveBaseLang(lang);
    const key = resolveDocKey(lvl, lang, baseLang);
    const rateStartedAt = new Date().toISOString();
    addLog({ type: 'info', message: `Rating: ${langName(lang)} ${lvl} (base: ${BASE_LANG_LABELS[baseLang] ?? baseLang}) via Claude API...` });

    const cell = getCellState(lang, lvl);
    const body: Record<string, unknown> = { base_lang: baseLang };
    const result = await apiCall('POST', `/api/admin/docs/${encodeURIComponent(key)}/rate`, body);
    if (result.ok) {
      const resp = result.data as { rating?: FullRating };
      if (resp.rating) applyRating(key, lang, lvl, resp.rating);
    } else if (result.status === 503 || result.status === 504) {
      addLog({ type: 'info', message: `API Gateway timeout — Lambda continues: exhaustive rating in progress (1-3 minutes)...` });
      let attempts = 0;
      const poll = async () => {
        attempts++;
        const check = await apiCall('GET', `/api/admin/docs/${encodeURIComponent(key)}`);
        if (check.ok) {
          const data = check.data as { rating?: FullRating; updated_at?: string };
          if (data.rating && data.updated_at && data.updated_at >= rateStartedAt) {
            applyRating(key, lang, lvl, data.rating);
            const r = data.rating;
            addLog({ type: 'info', message: `Rated: ${r.overall}/10 (acc=${r.accuracy?.score} cov=${r.coverage?.score} qty=${r.quantity?.score} trn=${r.transcription?.score})` });
            setRating(false);
            return;
          }
        }
        if (attempts < 168) {
          const elapsed = attempts * 5;
          addLog({ type: 'info', message: `Poll ${attempts}/168 (${elapsed}s) — rating not ready...` });
          setTimeout(poll, 5000);
        } else {
          addLog({ type: 'error', message: 'Rating timed out after 14 minutes of polling. Check Lambda logs.' });
          setRating(false);
        }
      };
      setTimeout(poll, 5000);
      return;
    }
    setRating(false);
  };

  const handleImprove = async () => {
    if (!selectedCell) return;
    setImproving(true);
    setPreflight(null);
    const { lang, level: lvl } = selectedCell;
    const baseLang = getEffectiveBaseLang(lang);
    const key = resolveDocKey(lvl, lang, baseLang);

    const content = docContent ?? await fetchDocContent(lang, lvl);
    if (!content) {
      addLog({ type: 'error', message: `Cannot improve: no document content for ${key}` });
      setImproving(false);
      return;
    }

    const instruction = improveInstruction.trim();

    // --- Custom instruction path: surgical diff ---
    if (instruction) {
      addLog({ type: 'info', message: `Surgical fix: "${instruction}" — Claude outputs edits, Lambda applies them...` });
      const fixStartedAt = new Date().toISOString();

      const cell = getCellState(lang, lvl);
      const body: Record<string, unknown> = {
        base_lang: baseLang,
        custom_instruction: instruction,
      };
        const result = await apiCall('POST', `/api/admin/docs/${encodeURIComponent(key)}/improve`, body);
      if (result.ok) {
        const resp = result.data as { content?: string; edits_applied?: number; edits_skipped?: number; edits_total?: number; edit_log?: string[] };
        if (resp.content) {
          setDocContent(resp.content);
          addLog({ type: 'info', message: `Fix complete: ${resp.edits_applied || 0}/${resp.edits_total || 0} edits applied, ${resp.edits_skipped || 0} skipped` });
          if (resp.edit_log) {
            for (const entry of resp.edit_log.slice(0, 20)) {
              addLog({ type: 'info', message: `  ${entry}` });
            }
            if (resp.edit_log.length > 20) addLog({ type: 'info', message: `  ... and ${resp.edit_log.length - 20} more` });
          }
          setImproveInstruction('');
        }
        setImproving(false);
        return;
      }
      if (result.status === 503 || result.status === 504) {
        addLog({ type: 'info', message: `API Gateway timeout — Lambda still processing. Polling (expect 1-3 min)...` });
        let attempts = 0;
        const poll = async () => {
          attempts++;
          const check = await apiCall('GET', `/api/admin/docs/${encodeURIComponent(key)}`);
          if (check.ok) {
            const data = check.data as { content?: string; updated_at?: string };
            if (data.updated_at && data.updated_at >= fixStartedAt) {
              if (data.content) setDocContent(data.content);
              addLog({ type: 'info', message: `Fix complete for ${key}` });
              setImproveInstruction('');
              setImproving(false);
              return;
            }
          }
          if (attempts < 60) {
            addLog({ type: 'info', message: `Poll ${attempts} — fix in progress...` });
            setTimeout(poll, 5000);
          } else {
            addLog({ type: 'error', message: 'Fix timed out after 5 minutes. Check Lambda logs.' });
            setImproving(false);
          }
        };
        setTimeout(poll, 5000);
        return;
      }
      setImproving(false);
      return;
    }

    // --- Pre-flight + multi-stage path (no custom instruction) ---
    addLog({ type: 'info', message: `Pre-flight check: ${langName(lang)} ${lvl} (base: ${BASE_LANG_LABELS[baseLang] ?? baseLang})...` });

    let a0Content: string | null = null;
    if (lvl === 'A1' || lvl === 'A2') {
      a0Content = await fetchDocContent(lang, 'A0');
    }

    await patternsReady;
    const validation = validateCefrDoc(lvl, lang, content, a0Content, baseLang);
    setPreflight(validation);

    if (validation.allPassed) {
      addLog({ type: 'info', message: `Pre-flight: ALL PASS (${validation.checks.length} checks). No deficiencies to fix.` });
      setImproving(false);
      return;
    }

    const failedChecks = validation.checks.filter(c => !c.pass);
    addLog({ type: 'info', message: `Pre-flight: ${failedChecks.length} failed checks. Sending fix report to improve endpoint...` });
    for (const c of failedChecks) {
      addLog({ type: 'info', message: `  [FAIL] ${c.label}: ${c.detail}` });
    }

    const improveStartedAt = new Date().toISOString();
    addLog({ type: 'info', message: `Improving: ${langName(lang)} ${lvl} — fixing ${failedChecks.length} deficiencies via Claude API...` });

    const cell = getCellState(lang, lvl);
    const body: Record<string, unknown> = {
      base_lang: baseLang,
      fix_report: validation.fixReport,
    };
    const result = await apiCall('POST', `/api/admin/docs/${encodeURIComponent(key)}/improve`, body);
    if (result.ok) {
      const resp = result.data as { content?: string; issues_fixed?: number | string; new_rating?: FullRating | null; pending?: boolean; message?: string; original_length?: number; improved_length?: number };
      if (resp.pending) {
        addLog({ type: 'error', message: `PENDING: ${resp.message}` });
        addLog({ type: 'info', message: `Use Confirm/Discard buttons to accept or reject the smaller result.` });
        setPendingKey(key);
      } else if (resp.content) {
        setDocContent(resp.content);
        const fixLabel = resp.issues_fixed === 'pre-flight' ? `${failedChecks.length} pre-flight deficiencies addressed` : `${resp.issues_fixed || 0} issues fixed`;
        addLog({ type: 'info', message: `Improved ${key}: ${fixLabel}` });
      }
      if (resp.new_rating) applyRating(key, lang, lvl, resp.new_rating);
    } else if (result.status === 503 || result.status === 504) {
      addLog({ type: 'info', message: `API Gateway timeout — Lambda continues multi-stage improve. Polling for progress...` });
      let attempts = 0;
      let lastStage = '';
      let staleCount = 0;
      const poll = async () => {
        attempts++;
        const check = await apiCall('GET', `/api/admin/docs/${encodeURIComponent(key)}`);
        if (check.ok) {
          const data = check.data as { content?: string; rating?: FullRating; updated_at?: string; pending_content?: string; pending_at?: string; improve_stage?: string };
          if (data.pending_content && data.pending_at && data.pending_at >= improveStartedAt) {
            addLog({ type: 'error', message: `SHRINKAGE GUARD: Result is smaller than original. Confirm or discard.` });
            setPendingKey(key);
            setImproving(false);
            return;
          }
          if (data.updated_at && data.updated_at >= improveStartedAt && !data.improve_stage) {
            if (data.content) setDocContent(data.content);
            if (data.rating) {
              applyRating(key, lang, lvl, data.rating);
              const r = data.rating;
              addLog({ type: 'info', message: `Improve + re-rate complete: ${r.overall}/10 (acc=${r.accuracy?.score} cov=${r.coverage?.score} qty=${r.quantity?.score} trn=${r.transcription?.score})` });
            } else {
              addLog({ type: 'info', message: `Improve complete (content updated)` });
            }
            setImproving(false);
            return;
          }
          const stage = data.improve_stage || '';
          if (stage && stage !== lastStage) {
            addLog({ type: 'info', message: `Poll ${attempts} — chunk ${stage} processing...` });
            lastStage = stage;
            staleCount = 0;
          } else {
            staleCount++;
          }
          if (staleCount >= 36) {
            addLog({ type: 'error', message: `Lambda appears to have timed out — no progress for 3 minutes. Content is unchanged.` });
            setImproving(false);
            return;
          }
        }
        if (attempts < 240) {
          setTimeout(poll, 5000);
        } else {
          addLog({ type: 'error', message: 'Improve timed out after 20 minutes of polling.' });
          setImproving(false);
        }
      };
      setTimeout(poll, 5000);
      return;
    }
    setImproving(false);
  };

  const handleFixApply = async () => {
    if (!selectedCell || !fixSection) return;
    setShowFixModal(false);
    setImproving(true);
    const { lang, level: lvl, baseLang: cellBaseLang } = selectedCell;
    const bl = cellBaseLang || 'en';
    const baseLang = getEffectiveBaseLang(lang);
    const lookupKey = bl !== 'en' ? `${lvl}_${lang}__${bl}` : `${lvl}_${lang}`;
    const mapEntry = generatedDocs.get(lookupKey);
    const key = mapEntry?.key || lookupKey;

    const content = docContent;
    if (!content) {
      addLog({ type: 'error', message: `Cannot fix: no document content for ${key}` });
      setImproving(false);
      return;
    }

    const issueLabels = fixIssues.length > 0 ? fixIssues.join(', ') : 'none specified';
    addLog({ type: 'info', message: `Surgical fix: §${fixSection} — issues: ${issueLabels}, type: ${fixType || 'none'} — extracting section, sending to Claude...` });

    const cell = getCellState(lang, lvl, bl);
    const body: Record<string, unknown> = {
      base_lang: bl !== 'en' ? bl : baseLang,
      fix_section: fixSection,
      fix_issues: fixIssues,
      fix_type: fixType || undefined,
      extra_instructions: fixInstructions.trim() || undefined,
    };

    const fixStartedAt = new Date().toISOString();
    const originalLength = content.length;
    const result = await apiCall('POST', `/api/admin/docs/${encodeURIComponent(key)}/improve`, body);
    if (result.ok) {
      const resp = result.data as { content?: string; section?: string; original_section_lines?: number; fixed_section_lines?: number; error?: string; shrinkage_download?: boolean; mechanical?: boolean; remaining_uncategorized?: number };
      if (resp.content) {
        if (resp.content.length < originalLength) {
          const filename = `${key}_fix_${fixSection}_${new Date().toISOString().replace(/[:.]/g, '-')}.md`;
          addLog({ type: 'error', message: `SHRINKAGE GUARD: Document shrank from ${originalLength} to ${resp.content.length} chars (${Math.round((1 - resp.content.length / originalLength) * 100)}% smaller). Original is preserved. The fixed content has been downloaded as ${filename} — check your Downloads folder.` });
          const blob = new Blob([resp.content], { type: 'text/markdown;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          setDocContent(resp.content);
          if (resp.mechanical) {
            addLog({ type: 'info', message: `Mechanical operation complete: §${resp.section} — ${resp.original_section_lines} lines → ${resp.fixed_section_lines} lines` });
          } else if (resp.remaining_uncategorized !== undefined) {
            addLog({ type: 'info', message: `Categorized 10 blocks, ${resp.remaining_uncategorized} remaining — run again to continue` });
          } else {
            addLog({ type: 'info', message: `Fix complete: §${resp.section} — ${resp.original_section_lines} lines → ${resp.fixed_section_lines} lines` });
          }
        }
        setFixSection('');
        setFixIssues([]);
        setFixType('');
        setFixInstructions('');
      } else if (resp.error) {
        addLog({ type: 'error', message: resp.error });
      }
      setImproving(false);
      return;
    }
    if (result.status === 503 || result.status === 504) {
      addLog({ type: 'info', message: `API Gateway timeout — Lambda still processing section. Polling...` });
      let attempts = 0;
      const poll = async () => {
        attempts++;
        const check = await apiCall('GET', `/api/admin/docs/${encodeURIComponent(key)}`);
        if (check.ok) {
          const data = check.data as { content?: string; updated_at?: string };
          if (data.updated_at && data.updated_at >= fixStartedAt) {
            if (data.content) setDocContent(data.content);
            addLog({ type: 'info', message: `Section fix complete for ${key}` });
            setImproving(false);
            return;
          }
        }
        if (attempts < 60) {
          addLog({ type: 'info', message: `Poll ${attempts} — section fix in progress...` });
          setTimeout(poll, 5000);
        } else {
          addLog({ type: 'error', message: 'Section fix timed out after 5 minutes.' });
          setImproving(false);
        }
      };
      setTimeout(poll, 5000);
      return;
    }
    setImproving(false);
  };

  const handleQASection = async () => {
    if (!selectedCell || !fixSection) return;
    setShowFixModal(false);
    setQaing(true);
    const { lang, level: lvl, baseLang: cellBaseLang } = selectedCell;
    const bl = cellBaseLang || 'en';
    const baseLang = getEffectiveBaseLang(lang);
    const lookupKey = bl !== 'en' ? `${lvl}_${lang}__${bl}` : `${lvl}_${lang}`;
    const mapEntry = generatedDocs.get(lookupKey);
    const key = mapEntry?.key || lookupKey;

    const content = docContent;
    if (!content) {
      addLog({ type: 'error', message: `Cannot QA: no document content for ${key}` });
      setQaing(false);
      return;
    }

    addLog({ type: 'info', message: `Section QA: §${fixSection} — ${langName(lang)} ${lvl} [${bl}] — extracting section, sending to Claude for 8-dimension review...` });

    const cell = getCellState(lang, lvl, bl);
    const body: Record<string, unknown> = {
      base_lang: bl !== 'en' ? bl : baseLang,
      qa_section: fixSection,
    };

    const qaStartedAt = new Date().toISOString();
    const originalLength = content.length;
    const result = await apiCall('POST', `/api/admin/docs/${encodeURIComponent(key)}/qa`, body);
    if (result.ok) {
      const resp = result.data as { content?: string; section?: string; original_section_lines?: number; fixed_section_lines?: number; issues_fixed?: number; qa_log?: string; shrinkage_detected?: boolean; error?: string };
      if (resp.content) {
        if (resp.shrinkage_detected || resp.content.length < originalLength) {
          const filename = `${key}_qa_${fixSection}_${new Date().toISOString().replace(/[:.]/g, '-')}.md`;
          addLog({ type: 'error', message: `SHRINKAGE GUARD: Document shrank from ${originalLength} to ${resp.content.length} chars (${Math.round((1 - resp.content.length / originalLength) * 100)}% smaller). Original is preserved. The QA result has been downloaded as ${filename} — check your Downloads folder.` });
          const blob = new Blob([resp.content], { type: 'text/markdown;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          setDocContent(resp.content);
          addLog({ type: 'info', message: `Section QA complete: §${resp.section} — ${resp.original_section_lines} lines → ${resp.fixed_section_lines} lines, ${resp.issues_fixed || 0} issues fixed` });
        }
        if (resp.qa_log) addLog({ type: 'info', message: resp.qa_log });
        setFixSection('');
      } else if (resp.error) {
        addLog({ type: 'error', message: resp.error });
      }
      setQaing(false);
      return;
    }
    if (result.status === 503 || result.status === 504) {
      addLog({ type: 'info', message: `API Gateway timeout — Lambda still processing section QA. Polling...` });
      let attempts = 0;
      const poll = async () => {
        attempts++;
        const check = await apiCall('GET', `/api/admin/docs/${encodeURIComponent(key)}`);
        if (check.ok) {
          const data = check.data as { content?: string; updated_at?: string };
          if (data.updated_at && data.updated_at >= qaStartedAt) {
            if (data.content) setDocContent(data.content);
            addLog({ type: 'info', message: `Section QA complete for ${key}` });
            setQaing(false);
            return;
          }
        }
        if (attempts < 60) {
          addLog({ type: 'info', message: `Poll ${attempts} — section QA in progress...` });
          setTimeout(poll, 5000);
        } else {
          addLog({ type: 'error', message: 'Section QA timed out after 5 minutes.' });
          setQaing(false);
        }
      };
      setTimeout(poll, 5000);
      return;
    }
    setQaing(false);
  };

  const handleQA = async () => {
    if (!selectedCell) return;
    setQaing(true);
    const { lang, level: lvl } = selectedCell;
    const baseLang = getEffectiveBaseLang(lang);
    const key = resolveDocKey(lvl, lang, baseLang);

    const content = docContent ?? await fetchDocContent(lang, lvl);
    if (!content) {
      addLog({ type: 'error', message: `Cannot QA: no document content for ${key}` });
      setQaing(false);
      return;
    }

    addLog({ type: 'info', message: `QA: ${langName(lang)} ${lvl} (base: ${BASE_LANG_LABELS[baseLang] ?? baseLang}) — checking native accuracy, vocalizations, glosses, transcription, translation, hallucinations, Unicode...` });

    const qaStartedAt = new Date().toISOString();
    const cell = getCellState(lang, lvl);
    const body: Record<string, unknown> = {
      base_lang: baseLang,
    };
    const result = await apiCall('POST', `/api/admin/docs/${encodeURIComponent(key)}/qa`, body);
    if (result.ok) {
      const resp = result.data as { content?: string; issues_fixed?: number; new_rating?: FullRating | null; qa_log?: string; pending?: boolean; message?: string; original_length?: number; improved_length?: number };
      if (resp.pending) {
        setPendingKey(key);
        addLog({ type: 'error', message: `SHRINKAGE GUARD: ${resp.message}` });
      } else if (resp.content) {
        setDocContent(resp.content);
        addLog({ type: 'info', message: `QA complete: ${resp.issues_fixed || 0} issues fixed` });
      }
      if (resp.qa_log) addLog({ type: 'info', message: resp.qa_log });
      if (resp.new_rating) applyRating(key, lang, lvl, resp.new_rating);
    } else if (result.status === 503 || result.status === 504) {
      addLog({ type: 'info', message: `API Gateway timeout — Lambda continues multi-stage QA. Polling for progress...` });
      let attempts = 0;
      let lastQaStage = '';
      let qaStaleCount = 0;
      const poll = async () => {
        attempts++;
        const check = await apiCall('GET', `/api/admin/docs/${encodeURIComponent(key)}`);
        if (check.ok) {
          const data = check.data as { content?: string; rating?: FullRating; updated_at?: string; pending_content?: string; pending_at?: string; improve_stage?: string };
          if (data.pending_content && data.pending_at && data.pending_at >= qaStartedAt) {
            setPendingKey(key);
            addLog({ type: 'error', message: `SHRINKAGE GUARD: QA result is smaller than original. Confirm or discard.` });
            setQaing(false);
            return;
          }
          if (data.updated_at && data.updated_at >= qaStartedAt && !data.improve_stage) {
            if (data.content) setDocContent(data.content);
            if (data.rating) {
              applyRating(key, lang, lvl, data.rating);
              addLog({ type: 'info', message: `QA + re-rate complete: ${data.rating.overall}/10` });
            } else {
              addLog({ type: 'info', message: `QA complete (content updated)` });
            }
            setQaing(false);
            return;
          }
          const stage = data.improve_stage || '';
          if (stage && stage !== lastQaStage) {
            addLog({ type: 'info', message: `Poll ${attempts} — ${stage} processing...` });
            lastQaStage = stage;
            qaStaleCount = 0;
          } else {
            qaStaleCount++;
          }
          if (qaStaleCount >= 36) {
            addLog({ type: 'error', message: `Lambda appears to have timed out — no progress for 3 minutes. Content is unchanged.` });
            setQaing(false);
            return;
          }
        }
        if (attempts < 240) {
          setTimeout(poll, 5000);
        } else {
          addLog({ type: 'error', message: 'QA timed out after 20 minutes.' });
          setQaing(false);
        }
      };
      setTimeout(poll, 5000);
      return;
    }
    setQaing(false);
  };

  const handleGenerateNew = async () => {
    const code = newLangCode.trim().toUpperCase();
    const name = newLangName.trim();
    if (!code || !name) return;

    setCustomLangName(code, name);
    setShowNewLangDialog(false);
    setSelectedCell({ lang: code, level: newLangLevel });
    setGenerating(true);
    setDocContent(null);
    setCurrentRating(null);
    setPreflight(null);

    const payload: Record<string, unknown> = {
      language_code: code,
      level: newLangLevel,
      language_name: name,
      base_lang: newLangBase,
      doc_type: newLangLevel === 'A0' ? 'phonics' : 'cefr',
    };
    const stageCount = newLangLevel === 'A0' ? 1 : 9;
    addLog({ type: 'info', message: `Generating NEW: ${name} (${code}) ${newLangLevel} (base: ${BASE_LANG_LABELS[newLangBase] ?? newLangBase}) — ${stageCount} API calls...` });

    const generateStartedAt = new Date().toISOString();
    const result = await apiCall('POST', '/api/admin/docs/generate', payload);

    if (result.ok) {
      const resp = result.data as { content?: string; key?: string; status?: string; rating?: FullRating; stages?: number; usage?: { input_tokens?: number; output_tokens?: number } };
      if (resp.content) {
        setDocContent(resp.content);
        const key = `${newLangLevel}_${code}`;
        const rScores: RatingScores | undefined = resp.rating ? { overall: resp.rating.overall, accuracy: resp.rating.accuracy?.score, coverage: resp.rating.coverage?.score, quantity: resp.rating.quantity?.score, transcription: resp.rating.transcription?.score } : undefined;
        setGeneratedDocs((prev) => {
          const next = new Map(prev);
          next.set(key, { key, language_code: code, level: newLangLevel, status: 'draft', rating: rScores });
          return next;
        });
        if (resp.rating) applyRating(key, code, newLangLevel, resp.rating);
        addLog({ type: 'info', message: `Generated ${name} ${newLangLevel}: ${resp.content.length} chars` });
      }
    } else if (result.status === 503 || result.status === 504) {
      addLog({ type: 'info', message: `API Gateway timeout — Lambda continues. Polling...` });
      const key = `${newLangLevel}_${code}`;
      let attempts = 0;
      const poll = async () => {
        attempts++;
        const check = await apiCall('GET', `/api/admin/docs/${encodeURIComponent(key)}`);
        if (check.ok) {
          const data = check.data as { content?: string; status?: string; rating?: FullRating; updated_at?: string; generate_stage?: string | number };
          if (data.updated_at && data.updated_at >= generateStartedAt && data.generate_stage === 'complete') {
            if (data.content) setDocContent(data.content);
            const rScores: RatingScores | undefined = data.rating ? { overall: data.rating.overall, accuracy: data.rating.accuracy?.score, coverage: data.rating.coverage?.score, quantity: data.rating.quantity?.score, transcription: data.rating.transcription?.score } : undefined;
            setGeneratedDocs((prev) => {
              const next = new Map(prev);
              next.set(key, { key, language_code: code, level: newLangLevel, status: 'draft', rating: rScores });
              return next;
            });
            if (data.rating) applyRating(key, code, newLangLevel, data.rating);
            setGenerating(false);
            return;
          }
        }
        if (attempts < 168) {
          addLog({ type: 'info', message: `Poll ${attempts}/168 (${attempts * 5}s) — generating...` });
          setTimeout(poll, 5000);
        } else {
          addLog({ type: 'error', message: 'Generation timed out after 14 minutes.' });
          setGenerating(false);
        }
      };
      setTimeout(poll, 5000);
      return;
    }
    setGenerating(false);
  };

  const getNewLangPatternInfo = (): { found: boolean; pattern: string; title: string; gloLevels: string[] } | null => {
    const code = newLangCode.trim().toUpperCase();
    if (!code) return null;
    const config = getTranscriptionPatterns();
    if (!config) return null;
    for (const [key, p] of Object.entries(config.patterns)) {
      if (p.languages.indexOf(code) !== -1) {
        return { found: true, pattern: key, title: p.title, gloLevels: p.glo_levels };
      }
    }
    return { found: false, pattern: '', title: '', gloLevels: [] };
  };

  const FIX_SECTIONS = [
    { group: 'Multi-section', options: [
      { value: '1-6', label: '§1–§6 (all communicative functions)' },
    ]},
    { group: 'Sections 1–8', options: [
      { value: '1', label: '§1 Give & Request Information' },
      { value: '2', label: '§2 Express Attitudes' },
      { value: '3', label: '§3 Get Things Done' },
      { value: '4', label: '§4 Socialise' },
      { value: '5', label: '§5 Structure Discourse' },
      { value: '6', label: '§6 Intercultural Competence' },
      { value: '7', label: '§7 Sociocultural Functions' },
      { value: '8', label: '§8 Language Awareness' },
    ]},
    { group: 'Section 9 — Topics', options: [
      { value: '9', label: '§9 All Topics' },
      { value: '9.1-9.5', label: '§9.1–§9.5' },
      { value: '9.6-9.10', label: '§9.6–§9.10' },
      { value: '9.11-9.15', label: '§9.11–§9.15' },
      { value: '9.16-9.20', label: '§9.16–§9.20' },
      { value: '9.21-9.25', label: '§9.21–§9.25' },
    ]},
    { group: 'Section 10 — Grammar', options: [
      { value: '10', label: '§10 All Grammar' },
      { value: '10.1', label: '§10.1 Verbs (general)' },
      { value: '10.2', label: '§10.2 Tense' },
      { value: '10.3', label: '§10.3 Aspect' },
      { value: '10.4', label: '§10.4 Mood & modality' },
      { value: '10.5', label: '§10.5 Nouns & arguments' },
      { value: '10.6', label: '§10.6 Adjectives' },
      { value: '10.7', label: '§10.7 Adverbs & phrases' },
      { value: '10.8', label: '§10.8 Sentence structure' },
      { value: '10.list', label: 'Grammar pattern list (uncategorized)' },
    ]},
  ];

  const FIX_ISSUES = [
    { group: 'Content Quality', options: [
      { value: 'vocalization', label: 'Vocalization' },
      { value: 'ipa', label: 'IPA' },
      { value: 'transcription', label: 'Transcription' },
      { value: 'register', label: 'Register' },
      { value: 'gloss', label: 'Gloss' },
      { value: 'translation', label: 'Translation' },
      { value: 'explanation', label: 'Explanation' },
    ]},
    { group: 'Section 9 Issues', options: [
      { value: '9_subsections', label: '§9 Counting/numbers subsections (數字)' },
      { value: '9_vocabulary', label: '§9 Vocabulary items' },
      { value: '9_patterns', label: '§9 Situation patterns' },
      { value: '9_dialogs', label: '§9 Mini-dialogs' },
    ]},
    { group: 'Section 10 Issues', options: [
      { value: '10_samples', label: '§10 <50 samples' },
      { value: '10_categorize', label: '§10 Assign §10.1–§10.8 to uncategorized patterns' },
      { value: '10_enrich', label: '§10 Add intro/explanation + ≥2 sample sentences per pattern' },
      { value: '10_organize', label: '§10 Organize into subsections & add intros' },
      { value: '10_grammar_db', label: '§10 Pull more grammar patterns & increase samples' },
    ]},
    { group: 'Section 10 Pipeline', options: [
      { value: '10_postprocess', label: '§10 Sort table + move §10.x column left (instant)' },
      { value: '10_move_rows', label: '§10 Move table rows to §10.x section (instant)' },
      { value: '10_strip_numbers', label: '§10 Strip §10.9+ numbering from text blocks (instant)' },
      { value: '10_categorize_blocks', label: '§10 Assign §10.1–§10.8 to text blocks (Claude, 10/batch)' },
      { value: '10_sort_blocks', label: '§10 Sort text blocks by §10.x (instant)' },
      { value: '10_move_blocks', label: '§10 Move text blocks to §10.x section (instant)' },
    ]},
  ];

  const FIX_TYPES = [
    { value: 'incorrect', label: 'Incorrect (has errors)' },
    { value: 'lacking', label: 'Lacking' },
    { value: 'wrong_order', label: 'Wrong order' },
    { value: 'wrong_format', label: 'Wrong format' },
    { value: 'too_few', label: 'Too few' },
    { value: 'lacking_transcription', label: 'Lacking transcription column' },
    { value: 'table_format', label: 'Requires table format' },
  ];

  const toggleFixIssue = (value: string) => {
    setFixIssues(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const renderFixModal = () => {
    if (!showFixModal || !selectedCell) return null;
    const fpFailed = fixPreflight ? fixPreflight.checks.filter(c => !c.pass) : [];
    const fpPassed = fixPreflight ? fixPreflight.checks.filter(c => c.pass) : [];
    const isQA = fixMode === 'qa';
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
        onClick={e => { if (e.target === e.currentTarget) setShowFixModal(false); }}>
        <div className="bg-zinc-800 border border-zinc-600 rounded-lg p-4 md:p-6 w-full max-w-[560px] mx-4 max-h-[90vh] overflow-y-auto">
          {/* Mode toggle */}
          <div className="flex gap-1 mb-4 bg-zinc-900 rounded-lg p-1">
            <button onClick={() => setFixMode('fix')}
              className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${!isQA ? 'bg-purple-700 text-purple-100' : 'text-zinc-400 hover:text-zinc-200'}`}>
              Fix
            </button>
            <button onClick={() => setFixMode('qa')}
              className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${isQA ? 'bg-teal-700 text-teal-100' : 'text-zinc-400 hover:text-zinc-200'}`}>
              QA
            </button>
          </div>

          <h2 className="font-heading text-xl font-bold text-zinc-100 mb-1">{isQA ? 'Section QA' : 'Surgical Fix'}</h2>
          <p className="text-xs text-zinc-500 mb-4">
            {langName(selectedCell.lang)} {selectedCell.level} — {isQA
              ? 'Claude reviews the target section across 8 quality dimensions and fixes issues in place.'
              : 'Claude reads only the target section, fixes it, and splices it back.'}
          </p>

          {/* Pre-flight results (fix mode only) */}
          {!isQA && !fixPreflight && (
            <div className="rounded-lg p-3 mb-4 bg-zinc-700/30 border border-zinc-600/50 text-xs text-zinc-400">
              Running pre-flight checks...
            </div>
          )}
          {!isQA && fixPreflight && (
            <div className={`rounded-lg p-3 mb-4 text-xs ${fixPreflight.allPassed ? 'bg-emerald-900/20 border border-emerald-800/30' : 'bg-red-900/20 border border-red-800/30'}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`font-mono font-bold ${fixPreflight.allPassed ? 'text-emerald-400' : 'text-red-400'}`}>
                  Pre-flight: {fixPreflight.allPassed ? 'ALL PASS' : `${fpFailed.length} FAILED`}
                </span>
                <span className="text-zinc-500">({fixPreflight.checks.length} checks)</span>
              </div>
              {fpFailed.length > 0 && (
                <ul className="space-y-0.5 mb-1">
                  {fpFailed.map(c => (
                    <li key={c.id} className="flex gap-2">
                      <span className="text-red-400 shrink-0">FAIL</span>
                      <span className="text-zinc-300">{c.label}: {c.detail}</span>
                    </li>
                  ))}
                </ul>
              )}
              {fpPassed.length > 0 && (
                <details className="text-zinc-500">
                  <summary className="cursor-pointer hover:text-zinc-400">{fpPassed.length} passed</summary>
                  <ul className="mt-1 space-y-0.5">
                    {fpPassed.map(c => (
                      <li key={c.id} className="flex gap-2">
                        <span className="text-emerald-600 shrink-0">PASS</span>
                        <span>{c.label}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {isQA && (
            <div className="rounded-lg p-3 mb-4 bg-teal-900/20 border border-teal-800/30 text-xs text-teal-300">
              8 dimensions: Native Accuracy, Vocalizations, Glosses, Transcription, Translation, Hallucinations, Unicode, Pattern Compliance
            </div>
          )}

          <div className="space-y-4">
            {/* Section */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1 font-medium">Section</label>
              <select value={fixSection} onChange={e => setFixSection(e.target.value)}
                className={`w-full px-3 py-2 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-sm focus:outline-none ${isQA ? 'focus:border-teal-500' : 'focus:border-purple-500'}`}>
                <option value="">Select a section...</option>
                {FIX_SECTIONS.map(g => (
                  <optgroup key={g.group} label={g.group}>
                    {g.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* Issues (fix mode only) */}
            {!isQA && (
              <div>
                <label className="block text-xs text-zinc-400 mb-1 font-medium">Issues</label>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {FIX_ISSUES.map(g => (
                    <div key={g.group} className="col-span-2">
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-2 mb-1">{g.group}</div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                        {g.options.map(o => (
                          <label key={o.value} className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer hover:text-zinc-100">
                            <input type="checkbox" checked={fixIssues.includes(o.value)} onChange={() => toggleFixIssue(o.value)}
                              className="rounded border-zinc-600 bg-zinc-700 text-purple-500 focus:ring-purple-500 focus:ring-offset-0" />
                            {o.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fix type (fix mode only) */}
            {!isQA && (
              <div>
                <label className="block text-xs text-zinc-400 mb-1 font-medium">Fix type</label>
                <select value={fixType} onChange={e => setFixType(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-sm focus:outline-none focus:border-purple-500">
                  <option value="">Select fix type...</option>
                  {FIX_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            )}

            {/* Extra instructions (fix mode only) */}
            {!isQA && (
              <div>
                <label className="block text-xs text-zinc-400 mb-1 font-medium">Extra instructions (optional)</label>
                <textarea value={fixInstructions} onChange={e => setFixInstructions(e.target.value)}
                  placeholder="e.g. swap [GLO] to appear before the translation"
                  rows={3}
                  className="w-full px-3 py-2 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-sm placeholder:text-zinc-500 focus:outline-none focus:border-purple-500 resize-none" />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setShowFixModal(false)}
              className="px-4 py-2 rounded text-sm text-zinc-400 hover:text-zinc-200">
              Cancel
            </button>
            <button onClick={isQA ? handleQASection : handleFixApply} disabled={!fixSection || improving || qaing}
              className={`px-4 py-2 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${isQA ? 'bg-teal-700 text-teal-100 hover:bg-teal-600' : 'bg-purple-700 text-purple-100 hover:bg-purple-600'}`}>
              {isQA ? 'Run QA' : 'Apply Fix'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderGenerateNewDialog = () => {
    if (!showNewLangDialog) return null;
    const patternInfo = getNewLangPatternInfo();
    const code = newLangCode.trim().toUpperCase();
    const canGenerate = code && newLangName.trim() && (!patternInfo || patternInfo.found);

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="bg-zinc-800 border border-zinc-600 rounded-lg p-4 md:p-6 w-full max-w-[480px] mx-4 max-h-[90vh] overflow-y-auto">
          <h2 className="font-heading text-xl font-bold text-zinc-100 mb-4">Generate New CEFR Document</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Target Language Code</label>
              <input
                type="text" value={newLangCode}
                onChange={e => setNewLangCode(e.target.value.toUpperCase())}
                placeholder="e.g. SWE, NLD, HRV"
                className="w-full px-3 py-2 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-sm font-mono placeholder:text-zinc-600 focus:outline-none focus:border-teal-500"
              />
            </div>

            <div>
              <label className="block text-xs text-zinc-400 mb-1">Language Name</label>
              <input
                type="text" value={newLangName}
                onChange={e => setNewLangName(e.target.value)}
                placeholder="e.g. Swedish, Dutch, Croatian"
                className="w-full px-3 py-2 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-teal-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">CEFR Level</label>
                <select value={newLangLevel} onChange={e => setNewLangLevel(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-sm focus:outline-none focus:border-teal-500">
                  {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Base Language</label>
                <select value={newLangBase} onChange={e => setNewLangBase(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-sm focus:outline-none focus:border-teal-500">
                  {ALL_BASE_LANGS.map(b => <option key={b.code} value={b.code}>{b.code} — {b.label}</option>)}
                </select>
              </div>
            </div>

            {/* Transcription pattern info */}
            {code && patternInfo && (
              patternInfo.found ? (
                <div className="rounded bg-teal-900/20 border border-teal-800/30 px-3 py-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-teal-400 font-mono font-bold">Pattern {patternInfo.pattern}</span>
                    <span className="text-zinc-400">{patternInfo.title}</span>
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">
                    [GLO] levels: {patternInfo.gloLevels.join(', ') || 'none'}
                  </div>
                </div>
              ) : (
                <div className="rounded bg-red-900/20 border border-red-800/30 px-3 py-2">
                  <p className="text-xs text-red-400 font-medium">
                    {code} is not assigned to any transcription pattern.
                  </p>
                  <p className="text-xs text-red-400/70 mt-1">
                    Go to <a href="/linguistics" className="underline text-red-300 hover:text-red-200">Linguistics</a> to add this language to a pattern (A–F) before generating.
                  </p>
                </div>
              )
            )}
            {code && !patternInfo && (
              <div className="rounded bg-amber-900/20 border border-amber-800/30 px-3 py-2">
                <p className="text-xs text-amber-400">Loading transcription patterns...</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setShowNewLangDialog(false)}
              className="px-4 py-2 rounded text-sm text-zinc-400 hover:text-zinc-200">
              Cancel
            </button>
            <button onClick={handleGenerateNew}
              disabled={!canGenerate || generating}
              className="px-4 py-2 rounded text-sm font-medium bg-teal-700 text-teal-100 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed">
              {generating ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const normalizedLevel = level?.toUpperCase();
  const filteredLevels = normalizedLevel ? [normalizedLevel] : LEVELS;
  const totalCells = allLangs.length * filteredLevels.length;
  const filledCells = allLangs.reduce((sum, lang) => sum + filteredLevels.filter((lvl) => getCellState(lang, lvl, 'en').status !== 'none').length, 0);
  const coveragePct = totalCells > 0 ? Math.round((filledCells / totalCells) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-zinc-500 font-mono text-sm">Loading CEFR coverage matrix...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-red-400 font-mono text-sm">{error}</p>
      </div>
    );
  }

  const statusDot = (status: DocStatus) => {
    switch (status) {
      case 'approved': return <span className="inline-block w-3 h-3 rounded-full bg-emerald-400" title="Approved" />;
      case 'draft': return <span className="inline-block w-3 h-3 rounded-full bg-amber-500/70" title="Draft" />;
      default: return <span className="inline-block w-3 h-3 rounded-full bg-zinc-700" title="Missing" />;
    }
  };

  const logTypeColor = (type: LogEntry['type']) => {
    switch (type) {
      case 'request': return 'text-blue-400';
      case 'response': return 'text-emerald-400';
      case 'error': return 'text-red-400';
      case 'info': return 'text-zinc-400';
    }
  };

  const logTypeLabel = (type: LogEntry['type']) => {
    switch (type) {
      case 'request': return 'REQ';
      case 'response': return 'RES';
      case 'error': return 'ERR';
      case 'info': return 'INF';
    }
  };

  const cellStatus = selectedCell ? getCellState(selectedCell.lang, selectedCell.level, selectedCell.baseLang).status : 'none';

  const renderDocPanel = () => (
    <>
      {(docLoading || generating || rating || improving || qaing) && (
        <p className="text-zinc-500 text-sm">{generating ? 'Generating via Claude API — staged build (8-12 min for CEFR, ~30s for A0)...' : rating ? 'Rating document via Claude API (1-3 min)...' : improving ? 'Improving document — fix + re-rate (3-8 min)...' : qaing ? 'QA review — checking quality across 7 dimensions (3-8 min)...' : 'Loading...'}</p>
      )}
      {renderRatingDetail()}
      {docContent && !docLoading && !generating && editing && (
        <div className="flex flex-col gap-2 mt-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-amber-400 font-medium">Editing — raw markdown</span>
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)}
                className="px-3 py-1 rounded text-xs text-zinc-400 hover:text-zinc-200">Cancel</button>
              <button onClick={handleSaveEdit}
                className="px-3 py-1 rounded text-xs font-medium bg-amber-700 text-amber-100 hover:bg-amber-600">Save</button>
            </div>
          </div>
          <textarea value={editContent} onChange={e => setEditContent(e.target.value)}
            className="w-full h-[50vh] md:h-[70vh] px-3 py-2 rounded bg-zinc-800 border border-zinc-600 text-zinc-200 text-xs font-mono leading-relaxed resize-none focus:outline-none focus:border-amber-500"
            spellCheck={false} />
        </div>
      )}
      {docContent && !docLoading && !generating && !editing && (
        <div className="overflow-y-auto">
          <div className="text-xs text-zinc-500 mb-2 font-mono">
            {docContent.length.toLocaleString()} chars · {docContent.split('\n').length.toLocaleString()} lines · {(docContent.match(/^## /gm) || []).length} sections
          </div>
          <div className="max-w-4xl font-body prose-mobile" dangerouslySetInnerHTML={{ __html: renderMarkdown(docContent) }} />
        </div>
      )}
      {!docContent && !docLoading && !generating && cellStatus === 'none' && (
        <p className="text-zinc-500 text-sm">No document yet. Click Generate to create one.</p>
      )}
    </>
  );

  const handleDownload = () => {
    if (!selectedCell || !docContent) return;
    const now = new Date(Date.now() + 8 * 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const ts = `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}_${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}`;
    const bl = selectedBaseLang || selectedCell.baseLang;
    const langSuffix = bl && bl !== 'en' ? `_${bl}` : '';
    const filename = `${selectedCell.lang}_${selectedCell.level}${langSuffix}_${ts}.md`;
    const blob = new Blob([docContent], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    addLog({ type: 'info', message: `Downloaded: ${filename}` });
  };

  const renderBaseLangSelector = () => {
    if (!selectedCell) return null;
    const effective = getEffectiveBaseLang(selectedCell.lang);
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-500">Base:</span>
        <select
          value={effective}
          onChange={e => setSelectedBaseLang(e.target.value)}
          className="px-2 py-1 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-xs font-mono focus:outline-none focus:border-teal-500"
        >
          {ALL_BASE_LANGS.map(b => (
            <option key={b.code} value={b.code}>{b.code} — {b.label}</option>
          ))}
        </select>
      </div>
    );
  };

  const renderPreflightResult = () => {
    if (!preflight) return null;
    const failed = preflight.checks.filter(c => !c.pass);
    const passed = preflight.checks.filter(c => c.pass);
    return (
      <div className={`rounded-lg p-3 mt-3 text-xs ${preflight.allPassed ? 'bg-emerald-900/20 border border-emerald-800/30' : 'bg-red-900/20 border border-red-800/30'}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className={`font-mono font-bold ${preflight.allPassed ? 'text-emerald-400' : 'text-red-400'}`}>
            Pre-flight: {preflight.allPassed ? 'ALL PASS' : `${failed.length} FAILED`}
          </span>
          <span className="text-zinc-500">({preflight.checks.length} checks)</span>
        </div>
        {failed.length > 0 && (
          <ul className="space-y-1 mb-2">
            {failed.map(c => (
              <li key={c.id} className="flex gap-2">
                <span className="text-red-400 shrink-0">FAIL</span>
                <span className="text-zinc-300">{c.label}: {c.detail}</span>
              </li>
            ))}
          </ul>
        )}
        {passed.length > 0 && (
          <details className="text-zinc-500">
            <summary className="cursor-pointer hover:text-zinc-400">{passed.length} passed checks</summary>
            <ul className="mt-1 space-y-0.5">
              {passed.map(c => (
                <li key={c.id} className="flex gap-2">
                  <span className="text-emerald-600 shrink-0">PASS</span>
                  <span>{c.label}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    );
  };

  const renderActionButtons = () => (
    <div className="flex gap-2 flex-wrap">
      <button onClick={() => handleGenerate(cellStatus !== 'none')} disabled={generating}
        className="px-3 py-1.5 rounded text-xs font-medium bg-teal-800 text-teal-200 hover:bg-teal-700 disabled:opacity-50">
        {generating ? 'Generating...' : cellStatus === 'none' ? 'Generate' : 'Regenerate'}
      </button>
      {cellStatus !== 'none' && (
        <>
          <button onClick={handleRate} disabled={rating || improving || qaing}
            className="px-3 py-1.5 rounded text-xs font-medium bg-blue-800 text-blue-200 hover:bg-blue-700 disabled:opacity-50">
            {rating ? 'Rating...' : 'Rate'}
          </button>
          <button onClick={handleApprove} disabled={improving || rating || qaing}
            className={`px-3 py-1.5 rounded text-xs font-medium disabled:opacity-50 ${cellStatus === 'approved' ? 'bg-amber-700 text-amber-100 hover:bg-amber-600' : 'bg-emerald-900 text-emerald-300 hover:bg-emerald-800'}`}>
            {cellStatus === 'approved' ? 'Draft' : 'Approve'}
          </button>
          <button onClick={handleQA} disabled={qaing || improving || rating}
            className="px-3 py-1.5 rounded text-xs font-medium bg-cyan-800 text-cyan-200 hover:bg-cyan-700 disabled:opacity-50">
            {qaing ? 'QA...' : 'QA'}
          </button>
          <button onClick={() => { setEditContent(docContent ?? ''); setEditing(true); }} disabled={!docContent || editing || improving || rating || qaing}
            className="px-3 py-1.5 rounded text-xs font-medium bg-amber-800 text-amber-200 hover:bg-amber-700 disabled:opacity-50">
            Edit
          </button>
          <button onClick={async () => {
            setFixPreflight(null);
            setShowFixModal(true);
            if (selectedCell) {
              const { lang, level: lvl } = selectedCell;
              const baseLang = getEffectiveBaseLang(lang);
              const content = docContent ?? await fetchDocContent(lang, lvl);
              if (content) {
                let a0Content: string | null = null;
                if (lvl === 'A1' || lvl === 'A2') a0Content = await fetchDocContent(lang, 'A0');
                await patternsReady;
                setFixPreflight(validateCefrDoc(lvl, lang, content, a0Content, baseLang));
              }
            }
          }} disabled={improving || rating || qaing}
            className="px-3 py-1.5 rounded text-xs font-medium bg-purple-800 text-purple-200 hover:bg-purple-700 disabled:opacity-50">
            {improving ? 'Fixing...' : 'Fix'}
          </button>
          {pendingKey && (
            <span className="px-2 py-1 rounded text-xs font-medium bg-amber-900 text-amber-200 animate-pulse">
              Pending review — see modal below
            </span>
          )}
          <button onClick={handleDownload} disabled={!docContent}
            className="px-3 py-1.5 rounded text-xs font-medium bg-zinc-700 text-zinc-200 hover:bg-zinc-600 disabled:opacity-50">
            Download
          </button>
          <button onClick={handleDiscard}
            className="px-3 py-1.5 rounded text-xs font-medium bg-red-800 text-red-200 hover:bg-red-700">
            Discard
          </button>
        </>
      )}
    </div>
  );

  const renderRatingBadge = (r: RatingScores | null | undefined) => {
    if (!r) return null;
    const color = r.overall >= 8 ? 'text-emerald-400' : r.overall >= 6 ? 'text-amber-400' : 'text-red-400';
    return <span className={`text-xs font-mono font-bold ${color}`}>{r.overall}/10</span>;
  };

  const renderRatingDetail = () => {
    if (!currentRating) return null;
    const rows = [
      { label: 'Accuracy', score: currentRating.accuracy.score, issues: currentRating.accuracy.issues },
      { label: 'Coverage', score: currentRating.coverage.score, issues: currentRating.coverage.issues },
      { label: 'Quantity', score: currentRating.quantity.score, issues: currentRating.quantity.issues },
      { label: 'Transcription', score: currentRating.transcription.score, issues: currentRating.transcription.issues },
    ];
    return (
      <div className="bg-zinc-800 rounded-lg p-3 mt-3 space-y-2">
        <div className="flex items-center gap-3">
          <span className="text-zinc-400 text-xs">Overall:</span>
          <span className={`font-mono font-bold ${currentRating.overall >= 8 ? 'text-emerald-400' : currentRating.overall >= 6 ? 'text-amber-400' : 'text-red-400'}`}>{currentRating.overall}/10</span>
        </div>
        {currentRating.summary && <p className="text-zinc-400 text-xs">{currentRating.summary}</p>}
        {rows.map((row) => (
          <div key={row.label}>
            <div className="flex items-center gap-2">
              <span className="text-zinc-500 text-xs w-24">{row.label}:</span>
              <span className={`font-mono text-xs font-bold ${row.score >= 8 ? 'text-emerald-400' : row.score >= 6 ? 'text-amber-400' : 'text-red-400'}`}>{row.score}/10</span>
            </div>
            {row.issues.length > 0 && (
              <ul className="ml-28 mt-0.5">
                {row.issues.map((issue, i) => <li key={i} className="text-red-400/70 text-[11px]">- {issue}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderConsole = () => (
    <div className="border border-zinc-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setConsoleOpen(!consoleOpen)}
        className="w-full flex items-center justify-between px-4 py-2 bg-zinc-800 hover:bg-zinc-750 text-left"
      >
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs font-bold text-zinc-300">API Console</span>
          <span className="font-mono text-[10px] text-zinc-500">{logs.length} entries</span>
          {logs.some((l) => l.type === 'error') && (
            <span className="font-mono text-[10px] text-red-400">{logs.filter((l) => l.type === 'error').length} errors</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleCancel(); }}
            disabled={!generating && !rating && !improving && !qaing}
            className="text-[10px] font-bold text-red-400 hover:text-red-300 px-2 py-0.5 rounded bg-red-900/40 hover:bg-red-900/60 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            CANCEL
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setExpandedLog(expandedLog === 'all' ? null : 'all'); }}
            className="text-[10px] text-zinc-500 hover:text-zinc-300 px-2 py-0.5 rounded bg-zinc-700"
          >
            {expandedLog === 'all' ? 'Collapse All' : 'Expand All'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setLogs([]); setExpandedLog(null); }}
            className="text-[10px] text-zinc-500 hover:text-zinc-300 px-2 py-0.5 rounded bg-zinc-700"
          >
            Clear
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const text = logs.map((l) => `[${l.ts}] [${logTypeLabel(l.type)}] ${l.message ?? ''}${l.requestBody ? '\n  Request: ' + l.requestBody : ''}${l.responseBody ? '\n  Response: ' + l.responseBody : ''}`).join('\n');
              navigator.clipboard.writeText(text);
            }}
            className="text-[10px] text-zinc-500 hover:text-zinc-300 px-2 py-0.5 rounded bg-zinc-700"
          >
            Copy All
          </button>
          <span className="text-zinc-500 text-xs">{consoleOpen ? '▼' : '▶'}</span>
        </div>
      </button>

      {consoleOpen && (
        <div ref={logContainerRef} className="bg-zinc-950 max-h-[300px] overflow-y-auto font-mono text-[11px] leading-5">
          {logs.length === 0 && (
            <div className="px-4 py-3 text-zinc-600">No activity yet. Click a language or action to see API calls.</div>
          )}
          {logs.map((entry) => (
            <div key={entry.id} className="border-b border-zinc-800/50">
              <div
                className={`px-4 py-1 flex items-start gap-3 cursor-pointer hover:bg-zinc-900/50 ${expandedLog === 'all' || expandedLog === entry.id ? 'bg-zinc-900/50' : ''}`}
                onClick={() => setExpandedLog(expandedLog === entry.id ? null : expandedLog === 'all' ? null : entry.id)}
              >
                <span className="text-zinc-600 shrink-0 w-[155px]">{entry.ts.slice(11)}</span>
                <span className={`shrink-0 w-7 font-bold ${logTypeColor(entry.type)}`}>{logTypeLabel(entry.type)}</span>
                {entry.method && <span className="text-yellow-500/70 shrink-0 w-10">{entry.method}</span>}
                {entry.status !== undefined && entry.status > 0 && (
                  <span className={`shrink-0 w-8 ${entry.status < 400 ? 'text-emerald-500' : 'text-red-500'}`}>{entry.status}</span>
                )}
                {entry.durationMs !== undefined && (
                  <span className={`shrink-0 w-14 text-right ${entry.durationMs > 2000 ? 'text-amber-500' : 'text-zinc-600'}`}>{entry.durationMs}ms</span>
                )}
                <span className={`flex-1 min-w-0 truncate ${logTypeColor(entry.type)}`}>
                  {entry.message}
                </span>
                {(entry.requestBody || entry.responseBody || entry.url) && (
                  <span className="text-zinc-600 shrink-0">{expandedLog === 'all' || expandedLog === entry.id ? '-' : '+'}</span>
                )}
              </div>
              {(expandedLog === 'all' || expandedLog === entry.id) && (entry.url || entry.requestBody || entry.responseBody) && (
                <div className="px-4 py-2 ml-[155px] space-y-2 bg-zinc-900/30 border-l-2 border-zinc-700">
                  {entry.url && (
                    <div>
                      <span className="text-zinc-600">URL: </span>
                      <span className="text-zinc-400 break-all">{entry.url}</span>
                    </div>
                  )}
                  {entry.requestBody && (
                    <div>
                      <span className="text-zinc-600">Request Body:</span>
                      <pre className="text-blue-400/70 mt-0.5 whitespace-pre-wrap break-all">{entry.requestBody}</pre>
                    </div>
                  )}
                  {entry.responseBody && (
                    <div>
                      <span className="text-zinc-600">Response Body:</span>
                      <pre className="text-zinc-400 mt-0.5 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">{entry.responseBody}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // --- Per-level view: language list + document reader ---
  if (normalizedLevel) {
    const langListVisible = !selectedCell;

    return (
      <div className="space-y-4 md:space-y-6">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-bold text-zinc-100">CEFR {normalizedLevel}</h1>
            <p className="text-zinc-400 text-sm mt-1">
              {allLangs.filter((l) => getCellState(l, normalizedLevel).status !== 'none').length}/{allLangs.length} languages
            </p>
          </div>
          <button onClick={() => { setNewLangLevel(normalizedLevel); setShowNewLangDialog(true); }}
            className="px-3 py-1.5 rounded text-xs font-medium bg-teal-800 text-teal-200 hover:bg-teal-700 shrink-0">
            + Generate New
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-4 md:gap-6" style={{ minHeight: '60vh' }}>
          {/* Language list — always visible on desktop, toggle on mobile */}
          <div className={`w-full md:w-64 shrink-0 overflow-y-auto border border-zinc-700 rounded-lg bg-zinc-900 md:max-h-none ${langListVisible ? 'max-h-[50vh]' : 'hidden md:block'}`}>
            {allLangs.map((lang) => {
              const cell = getCellState(lang, normalizedLevel);
              const isSelected = selectedCell?.lang === lang && selectedCell?.level === normalizedLevel;
              return (
                <div key={lang}
                  onClick={() => handleCellClick(lang, normalizedLevel)}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer border-b border-zinc-800/50 transition-colors ${isSelected ? 'bg-teal-900/30 border-l-2 border-l-teal-500' : 'hover:bg-zinc-800/50'}`}>
                  {statusDot(cell.status)}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-200 truncate">{langName(lang)}</div>
                    <div className="text-[10px] font-mono text-zinc-600">{lang}</div>
                  </div>
                  {renderRatingBadge(generatedDocs.get(`${normalizedLevel}_${lang}`)?.rating)}
                </div>
              );
            })}
          </div>

          {/* Document reader panel */}
          <div className={`flex-1 min-w-0 border border-zinc-700 rounded-lg bg-zinc-900 p-3 md:p-4 overflow-y-auto ${!selectedCell ? 'hidden md:block' : ''}`}>
            {selectedCell ? (
              <>
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4 pb-3 border-b border-zinc-700">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => { setSelectedCell(null); setDocContent(null); setPreflight(null); }}
                      className="md:hidden p-1 text-zinc-400 hover:text-zinc-200 -ml-1"
                      aria-label="Back to language list"
                    >
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3L6 9l6 6" /></svg>
                    </button>
                    <span className="text-lg text-zinc-100 font-medium">{langName(selectedCell.lang)}</span>
                    {statusDot(cellStatus)}
                    <span className="text-xs text-zinc-500">{cellStatus}</span>
                    {renderBaseLangSelector()}
                  </div>
                  {renderActionButtons()}
                </div>
                {renderPreflightResult()}
                {renderDocPanel()}
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-zinc-600 text-sm">
                Select a language to view its {normalizedLevel} document
              </div>
            )}
          </div>
        </div>

        {/* API Console */}
        {renderConsole()}
        {renderFixModal()}
        {renderGenerateNewDialog()}
      </div>
    );
  }

  // --- All-levels matrix view (CEFR All) ---
  return (
    <div className="space-y-6">
      {/* Tab bar */}
      <div className="flex items-center gap-0 border-b border-zinc-700 overflow-x-auto">
        <button onClick={() => setActiveTab('documents')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'documents' ? 'border-teal-500 text-teal-300' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          Documents
        </button>
        <button onClick={() => setActiveTab('fixqa')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'fixqa' ? 'border-teal-500 text-teal-300' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          FIX_QA
          {fixQaHistory.some(r => r.status === 'running') && <span className="inline-block w-2 h-2 rounded-full bg-teal-400 animate-pulse" />}
        </button>
        <button onClick={() => setActiveTab('localization')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'localization' ? 'border-purple-500 text-purple-300' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          Localization
          {locHistory.some(r => r.status === 'running') && <span className="inline-block w-2 h-2 rounded-full bg-purple-400 animate-pulse" />}
        </button>
        <button onClick={() => setActiveTab('generate')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'generate' ? 'border-sky-500 text-sky-300' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          Generate
          {genHistory.some(r => r.status === 'running') && <span className="inline-block w-2 h-2 rounded-full bg-sky-400 animate-pulse" />}
        </button>
      </div>

      {activeTab === 'fixqa' && (
        <div className="space-y-6">
          {/* Queue controls */}
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-zinc-100 mb-4">FIX_QA Pipeline</h2>
            <div className="flex flex-wrap items-end gap-3 md:gap-4 mb-4">
              <div className="w-full sm:w-auto">
                <label className="block text-xs text-zinc-400 mb-1">Language</label>
                <select value={fixQaLang} onChange={e => setFixQaLang(e.target.value)}
                  className="bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-sm text-zinc-200 w-full sm:w-auto">
                  {allLangs.map(l => <option key={l} value={l}>{langName(l)} ({l})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Level</label>
                <select value={fixQaLevel} onChange={e => setFixQaLevel(e.target.value)}
                  className="bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-sm text-zinc-200">
                  {LEVELS.filter(l => l !== 'A0').map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Section</label>
                <select value={fixQaSection} onChange={e => setFixQaSection(e.target.value)}
                  className="bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-sm text-zinc-200">
                  <option value="">All sections</option>
                  {Array.from({ length: 10 }, (_, i) => i + 1).map(n => <option key={n} value={String(n)}>§{n}</option>)}
                </select>
              </div>
              <button onClick={handleFixQaStart} disabled={fixQaStarting || fixQaHistory.some(r => r.doc_key === `cefr_${fixQaLevel}_${fixQaLang}` && (r.status === 'running' || r.status === 'scheduled'))}
                className="px-4 py-1.5 rounded text-sm font-medium bg-teal-700 text-teal-100 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed">
                {fixQaStarting ? 'Starting...' : 'Run Now'}
              </button>
            </div>

            {/* Schedule runs */}
            <div className="border-t border-zinc-700 pt-4 mt-2">
              <h3 className="text-sm font-medium text-zinc-300 mb-2">Schedule Runs (UTC+8)</h3>
              {fixQaSchedule.map((row, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 mb-2">
                  <select value={row.level} onChange={e => handleFixQaScheduleUpdate(i, 'level', e.target.value)}
                    className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-16">
                    {LEVELS.filter(l => l !== 'A0').map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                  <select value={row.language_code} onChange={e => handleFixQaScheduleUpdate(i, 'language_code', e.target.value)}
                    className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-40 sm:w-48">
                    {allLangs.map(l => <option key={l} value={l}>{langName(l)} ({l})</option>)}
                  </select>
                  <input type="date" value={row.date} onChange={e => handleFixQaScheduleUpdate(i, 'date', e.target.value)}
                    className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-32 sm:w-36" />
                  <input type="time" value={row.time} step="60" onChange={e => handleFixQaScheduleUpdate(i, 'time', e.target.value)}
                    className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-24" />
                  <button onClick={() => handleFixQaScheduleRemoveRow(i)}
                    className="text-zinc-500 hover:text-red-400 text-sm px-1">&times;</button>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <button onClick={handleFixQaScheduleAddRow}
                  className="px-3 py-1 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                  + Add Row
                </button>
                {fixQaSchedule.length > 0 && (
                  <>
                    <button onClick={handleFixQaScheduleSubmit} disabled={fixQaScheduleSubmitting}
                      className="px-3 py-1.5 rounded text-xs font-medium bg-amber-700 text-amber-100 hover:bg-amber-600 disabled:opacity-50">
                      {fixQaScheduleSubmitting ? 'Scheduling...' : `Schedule ${fixQaSchedule.length} runs`}
                    </button>
                    <button onClick={() => setFixQaSchedule([])}
                      className="px-3 py-1 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                      Clear
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Active runs — one panel per running/scheduled job */}
            {fixQaHistory.filter(r => r.status === 'running' || r.status === 'scheduled').map(run => (
              <div key={run.doc_key} className="bg-zinc-900 border border-teal-800/50 rounded-lg p-3 md:p-4 mt-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-teal-400 animate-pulse" />
                    <span className="text-zinc-100 font-medium">
                      {langName(run.language_code)} {run.level}
                    </span>
                    <span className="text-xs text-zinc-500">
                      Step {run.current_step + 1}/{run.total_steps}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => handleFixQaCancel(run.doc_key)}
                      className="px-2.5 py-1 rounded text-xs font-medium bg-amber-900/50 text-amber-300 hover:bg-amber-800/50">
                      Pause
                    </button>
                    <button onClick={() => handleFixQaRestart(run.doc_key, 0, '§1')}
                      className="px-2.5 py-1 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                      From §1
                    </button>
                    <button onClick={() => handleFixQaRestart(run.doc_key, 8, '§9')}
                      className="px-2.5 py-1 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                      From §9
                    </button>
                    <button onClick={() => handleFixQaRestart(run.doc_key, 33, '§10')}
                      className="px-2.5 py-1 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                      From §10
                    </button>
                    <button onClick={() => handleFixQaCancel(run.doc_key)}
                      className="px-2.5 py-1 rounded text-xs font-medium bg-red-900/50 text-red-300 hover:bg-red-800/50">
                      Cancel
                    </button>
                  </div>
                </div>
                <div className="text-sm text-teal-300 mb-2">
                  {run.current_section && `§${run.current_section}`} — {run.current_check}
                </div>
                <div className="w-full bg-zinc-700 rounded-full h-2 mb-2">
                  <div className="bg-teal-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${((run.current_step + 1) / run.total_steps) * 100}%` }} />
                </div>
                <div className="flex gap-4 text-xs text-zinc-500">
                  <span>Cost: ${run.cost_usd?.toFixed(4) ?? '0.0000'}</span>
                  {run.started_at && (
                    <span>Elapsed: {Math.round((Date.now() - new Date(run.started_at).getTime()) / 1000)}s</span>
                  )}
                  {run.status === 'scheduled' && run.scheduled_at && (
                    <span>Starts: {new Date(new Date(run.scheduled_at).getTime()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Taipei' })} UTC+8</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Run history */}
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-zinc-300 mb-3">Run History</h3>
            {fixQaHistory.length === 0 ? (
              <p className="text-xs text-zinc-500">No runs yet.</p>
            ) : (
              <div className="space-y-1">
                {fixQaHistory.map(run => (
                  <div key={run.doc_key + run.created_at}>
                    <div onClick={() => handleFixQaViewRun(run.doc_key)}
                      className="flex flex-wrap items-center gap-2 md:gap-3 px-2 md:px-3 py-2 rounded hover:bg-zinc-700/50 cursor-pointer text-sm">
                      <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                        run.status === 'completed' ? 'bg-emerald-400' :
                        run.status === 'failed' ? 'bg-red-500' :
                        run.status === 'running' ? 'bg-teal-400 animate-pulse' :
                        run.status === 'cancelled' ? 'bg-zinc-500' : 'bg-amber-500'
                      }`} />
                      <span className="text-zinc-300 font-mono text-xs">{run.doc_key.replace('cefr_', '')}</span>
                      <span className="text-zinc-500 text-xs">{run.status}</span>
                      <span className="text-zinc-600 text-xs">{run.current_step + 1}/{run.total_steps} steps</span>
                      <span className="text-zinc-600 text-xs">${run.cost_usd?.toFixed(4) ?? '0'}</span>
                      <span className="text-zinc-600 text-xs md:ml-auto">{run.created_at?.slice(0, 16)}</span>
                      {(run.status === 'failed' || run.status === 'cancelled' || run.status === 'completed') && (
                        <div className="flex gap-1 ml-2" onClick={e => e.stopPropagation()}>
                          <button onClick={() => handleFixQaRestart(run.doc_key, 0, '§1')}
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-zinc-700 text-zinc-400 hover:bg-zinc-600 hover:text-zinc-200">§1</button>
                          <button onClick={() => handleFixQaRestart(run.doc_key, 8, '§9')}
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-zinc-700 text-zinc-400 hover:bg-zinc-600 hover:text-zinc-200">§9</button>
                          <button onClick={() => handleFixQaRestart(run.doc_key, 33, '§10')}
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-zinc-700 text-zinc-400 hover:bg-zinc-600 hover:text-zinc-200">§10</button>
                        </div>
                      )}
                    </div>
                    {fixQaExpandedRun === run.doc_key && fixQaExpandedSteps[run.doc_key] && (
                      <div className="ml-8 mb-2 max-h-60 overflow-y-auto">
                        {fixQaExpandedSteps[run.doc_key]!.map(s => (
                          <div key={s.step_index} className="flex items-center gap-2 px-2 py-0.5 text-[11px]">
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                              s.status === 'completed' ? 'bg-emerald-400' :
                              s.status === 'failed' ? 'bg-red-500' :
                              s.status === 'running' ? 'bg-teal-400' : 'bg-zinc-600'
                            }`} />
                            <span className="text-zinc-500 w-6">{s.step_index + 1}</span>
                            <span className="text-zinc-400">{s.label}</span>
                            {s.cost_usd > 0 && <span className="text-zinc-600">${s.cost_usd.toFixed(4)}</span>}
                            {s.error && <span className="text-red-400 truncate max-w-xs">{s.error}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'localization' && (
        <div className="space-y-6">
          {/* Localization controls */}
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-zinc-100 mb-4">Localization Pipeline</h2>
            <div className="flex flex-wrap items-end gap-3 md:gap-4 mb-4">
              <div className="w-full sm:w-auto">
                <label className="block text-xs text-zinc-400 mb-1">Target Language</label>
                <select value={locLang} onChange={e => setLocLang(e.target.value)}
                  className="bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-sm text-zinc-200 w-full sm:w-auto">
                  {allLangs.map(l => <option key={l} value={l}>{langName(l)} ({l})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Level</label>
                <select value={locLevel} onChange={e => setLocLevel(e.target.value)}
                  className="bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-sm text-zinc-200">
                  {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Source Base</label>
                <select value={locSourceBase} onChange={e => setLocSourceBase(e.target.value)}
                  className="bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-sm text-zinc-200">
                  {ALL_BASE_LANGS.map(b => <option key={b.code} value={b.code}>{b.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Target Base</label>
                <select value={locTargetBase} onChange={e => setLocTargetBase(e.target.value)}
                  className="bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-sm text-zinc-200">
                  {ALL_BASE_LANGS.filter(b => b.code !== locSourceBase).map(b => <option key={b.code} value={b.code}>{b.label}</option>)}
                </select>
              </div>
              <button onClick={handleLocStart} disabled={locStarting || locHistory.some(r => r.doc_key === `loc_${locLevel}_${locLang}_${locTargetBase}` && (r.status === 'running' || r.status === 'scheduled'))}
                className="px-4 py-1.5 rounded text-sm font-medium bg-purple-700 text-purple-100 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed">
                {locStarting ? 'Starting...' : 'Run Now'}
              </button>
            </div>

            {/* Batch queue builder */}
            <div className="border-t border-zinc-700 pt-4 mt-2">
              <h3 className="text-sm font-medium text-zinc-300 mb-2">Queue Jobs</h3>
              {locSchedule.map((row, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 mb-2">
                  <select value={row.level} onChange={e => handleLocScheduleUpdate(i, 'level', e.target.value)}
                    className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-16">
                    {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                  <select value={row.language_code} onChange={e => handleLocScheduleUpdate(i, 'language_code', e.target.value)}
                    className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-40 sm:w-48">
                    {allLangs.map(l => <option key={l} value={l}>{langName(l)} ({l})</option>)}
                  </select>
                  <span className="text-zinc-500 text-xs">from</span>
                  <select value={row.source_base_lang} onChange={e => handleLocScheduleUpdate(i, 'source_base_lang', e.target.value)}
                    className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-28">
                    {ALL_BASE_LANGS.map(b => <option key={b.code} value={b.code}>{b.label}</option>)}
                  </select>
                  <span className="text-zinc-500 text-xs">&rarr;</span>
                  <select value={row.target_base_lang} onChange={e => handleLocScheduleUpdate(i, 'target_base_lang', e.target.value)}
                    className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-28">
                    {ALL_BASE_LANGS.map(b => <option key={b.code} value={b.code}>{b.label}</option>)}
                  </select>
                  <button onClick={() => handleLocScheduleRemoveRow(i)}
                    className="text-zinc-500 hover:text-red-400 text-sm px-1">&times;</button>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <button onClick={handleLocScheduleAddRow}
                  className="px-3 py-1 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                  + Add Row
                </button>
                {locSchedule.length > 0 && (
                  <>
                    <button onClick={handleLocQueueSubmit}
                      className="px-3 py-1.5 rounded text-xs font-medium bg-purple-700 text-purple-100 hover:bg-purple-600">
                      Queue {locSchedule.length} jobs
                    </button>
                    <button onClick={handleLocScheduleSubmit} disabled={locScheduleSubmitting}
                      className="px-3 py-1.5 rounded text-xs font-medium bg-amber-700 text-amber-100 hover:bg-amber-600 disabled:opacity-50">
                      {locScheduleSubmitting ? 'Scheduling...' : `Schedule ${locSchedule.length} runs`}
                    </button>
                    <button onClick={() => setLocSchedule([])}
                      className="px-3 py-1 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                      Clear
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Pending queue */}
            {locQueue.filter(q => q.status === 'pending').length > 0 && (
              <div className="border-t border-zinc-700 pt-3 mt-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-zinc-300">Pending Queue ({locQueue.filter(q => q.status === 'pending').length})</h3>
                  <button onClick={handleLocQueueClear}
                    className="px-2 py-0.5 rounded text-[10px] font-medium bg-zinc-700 text-zinc-400 hover:bg-zinc-600">
                    Clear Queue
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {locQueue.filter(q => q.status === 'pending').map((q, i) => (
                    <span key={i} className="px-2 py-0.5 rounded text-xs bg-zinc-700 text-zinc-300">
                      {q.level} {q.language_code} → {q.target_base_lang}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Active runs */}
            {locHistory.filter(r => r.status === 'running' || r.status === 'scheduled').map(run => (
              <div key={run.doc_key} className="bg-zinc-900 border border-purple-800/50 rounded-lg p-3 md:p-4 mt-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-purple-400 animate-pulse" />
                    <span className="text-zinc-100 font-medium">
                      {langName(run.language_code)} {run.level}
                    </span>
                    <span className="text-xs text-purple-300 font-mono">
                      {run.source_base_lang || 'en'} → {run.target_base_lang}
                    </span>
                    <span className="text-xs text-zinc-500">
                      Step {run.current_step + 1}/{run.total_steps}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => handleLocCancel(run.doc_key)}
                      className="px-2.5 py-1 rounded text-xs font-medium bg-amber-900/50 text-amber-300 hover:bg-amber-800/50">
                      Pause
                    </button>
                    <button onClick={() => handleLocRestart(run.doc_key, 0, 'Start')}
                      className="px-2.5 py-1 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                      Restart
                    </button>
                    <button onClick={() => handleLocCancel(run.doc_key)}
                      className="px-2.5 py-1 rounded text-xs font-medium bg-red-900/50 text-red-300 hover:bg-red-800/50">
                      Cancel
                    </button>
                  </div>
                </div>
                <div className="text-sm text-purple-300 mb-2">
                  {run.current_section && `§${run.current_section}`} — {run.current_check}
                </div>
                <div className="w-full bg-zinc-700 rounded-full h-2 mb-2">
                  <div className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${((run.current_step + 1) / run.total_steps) * 100}%` }} />
                </div>
                <div className="flex gap-4 text-xs text-zinc-500">
                  <span>Cost: ${run.cost_usd?.toFixed(4) ?? '0.0000'}</span>
                  {run.started_at && (
                    <span>Elapsed: {Math.round((Date.now() - new Date(run.started_at).getTime()) / 1000)}s</span>
                  )}
                  {run.status === 'scheduled' && run.scheduled_at && (
                    <span>Starts: {new Date(run.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Taipei' })} UTC+8</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Run history */}
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-zinc-300 mb-3">Localization History</h3>
            {locHistory.length === 0 ? (
              <p className="text-xs text-zinc-500">No localization runs yet.</p>
            ) : (
              <div className="space-y-1">
                {locHistory.map(run => (
                  <div key={run.doc_key + run.created_at}>
                    <div onClick={() => handleLocViewRun(run.doc_key)}
                      className="flex flex-wrap items-center gap-2 md:gap-3 px-2 md:px-3 py-2 rounded hover:bg-zinc-700/50 cursor-pointer text-sm">
                      <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                        run.status === 'completed' ? 'bg-emerald-400' :
                        run.status === 'failed' ? 'bg-red-500' :
                        run.status === 'running' ? 'bg-purple-400 animate-pulse' :
                        run.status === 'cancelled' ? 'bg-zinc-500' : 'bg-amber-500'
                      }`} />
                      <span className="text-zinc-300 font-mono text-xs">{run.doc_key.replace('loc_', '')}</span>
                      <span className="text-purple-300 text-xs font-mono">{run.source_base_lang || 'en'}→{run.target_base_lang}</span>
                      <span className="text-zinc-500 text-xs">{run.status}</span>
                      <span className="text-zinc-600 text-xs">{run.current_step + 1}/{run.total_steps} steps</span>
                      <span className="text-zinc-600 text-xs">${run.cost_usd?.toFixed(4) ?? '0'}</span>
                      <span className="text-zinc-600 text-xs md:ml-auto">{run.created_at?.slice(0, 16)}</span>
                      {(run.status === 'failed' || run.status === 'cancelled' || run.status === 'completed') && (
                        <div className="flex gap-1 ml-2" onClick={e => e.stopPropagation()}>
                          <button onClick={() => handleLocRestart(run.doc_key, 0, 'Start')}
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-zinc-700 text-zinc-400 hover:bg-zinc-600 hover:text-zinc-200">Restart</button>
                        </div>
                      )}
                    </div>
                    {locExpandedRun === run.doc_key && locExpandedSteps[run.doc_key] && (
                      <div className="ml-8 mb-2 max-h-60 overflow-y-auto">
                        {locExpandedSteps[run.doc_key]!.map(s => (
                          <div key={s.step_index} className="flex items-center gap-2 px-2 py-0.5 text-[11px]">
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                              s.status === 'completed' ? 'bg-emerald-400' :
                              s.status === 'failed' ? 'bg-red-500' :
                              s.status === 'running' ? 'bg-purple-400' : 'bg-zinc-600'
                            }`} />
                            <span className="text-zinc-500 w-6">{s.step_index + 1}</span>
                            <span className="text-zinc-400">{s.label}</span>
                            {s.cost_usd > 0 && <span className="text-zinc-600">${s.cost_usd.toFixed(4)}</span>}
                            {s.error && <span className="text-red-400 truncate max-w-xs">{s.error}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'generate' && (
        <div className="space-y-6">
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-zinc-100 mb-4">Generate CEFR Documents</h2>
            <p className="text-xs text-zinc-400 mb-4">
              Queue languages for CEFR document generation. Tier 1 &amp; 2 use claude-opus-4-6, Tier 3 &amp; 4 use claude-opus-4-7.
              {!tiersLoaded && ' Loading language tiers...'}
            </p>

            {/* Schedule builder (UTC+8) */}
            <h3 className="text-sm font-medium text-zinc-300 mb-2">Schedule Runs (UTC+8)</h3>
            <div className="space-y-2 mb-4">
              {genSchedule.map((row, i) => {
                const tier = getTierForLang(row.language_code);
                const model = getModelForTier(tier);
                return (
                  <div key={i} className="space-y-1 mb-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <LangSearchSelect
                        options={tierLangs.map(t => ({ code: t.language_code, label: `${t.display_name} — T${t.tier}` }))}
                        value={row.language_code}
                        onChange={v => handleGenScheduleUpdate(i, 'language_code', v)}
                        className="w-56"
                      />
                      <select value={row.level} onChange={e => handleGenScheduleUpdate(i, 'level', e.target.value)}
                        className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-16">
                        {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                      <select value={row.base_lang} onChange={e => handleGenScheduleUpdate(i, 'base_lang', e.target.value)}
                        className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-40">
                        {ALL_BASE_LANGS.map(b => (
                          <option key={b.code} value={b.code}>{b.label}</option>
                        ))}
                      </select>
                      <input type="date" value={row.date} onChange={e => handleGenScheduleUpdate(i, 'date', e.target.value)}
                        className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-32 sm:w-36" />
                      <input type="time" value={row.time} step="60" onChange={e => handleGenScheduleUpdate(i, 'time', e.target.value)}
                        className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200 w-24" />
                      {tier > 0 && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${tier <= 2 ? 'bg-emerald-900/50 text-emerald-300' : 'bg-amber-900/50 text-amber-300'}`}>
                          T{tier} → {model}
                        </span>
                      )}
                      <button onClick={() => handleGenScheduleRemoveRow(i)}
                        className="text-zinc-500 hover:text-red-400 text-sm px-1">&times;</button>
                    </div>
                    <input type="text" value={row.custom_instructions}
                      onChange={e => handleGenScheduleUpdate(i, 'custom_instructions', e.target.value)}
                      placeholder="Custom instructions (e.g. 'Use traditional Javanese script throughout')"
                      className="bg-zinc-700/50 border border-zinc-600/50 rounded px-2 py-1 text-xs text-zinc-300 placeholder:text-zinc-600 w-full" />
                  </div>
                );
              })}
              <div className="flex gap-2 mt-2">
                <button onClick={handleGenScheduleAddRow} disabled={!tiersLoaded}
                  className="px-3 py-1 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600 disabled:opacity-50">
                  + Add Row
                </button>
                {genSchedule.length > 0 && (
                  <>
                    <button onClick={handleGenQueueSubmit} disabled={genScheduleSubmitting}
                      className="px-3 py-1.5 rounded text-xs font-medium bg-sky-700 text-sky-100 hover:bg-sky-600 disabled:opacity-50">
                      {genScheduleSubmitting ? 'Scheduling...' : `Schedule ${genSchedule.length} documents`}
                    </button>
                    <button onClick={() => setGenSchedule([])}
                      className="px-3 py-1 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                      Clear
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Pending queue */}
            {genQueue.filter(q => q.status === 'pending').length > 0 && (
              <div className="border-t border-zinc-700 pt-3 mt-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-zinc-300">Pending Queue ({genQueue.filter(q => q.status === 'pending').length})</h3>
                  <button onClick={handleGenQueueClear}
                    className="px-2 py-0.5 rounded text-[10px] font-medium bg-zinc-700 text-zinc-400 hover:bg-zinc-600">
                    Clear Queue
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {genQueue.filter(q => q.status === 'pending').map((q, i) => (
                    <span key={i} className="px-2 py-0.5 rounded text-xs bg-zinc-700 text-zinc-300">
                      {q.level} {q.language_name || q.language_code}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Active runs */}
            {genHistory.filter(r => r.status === 'running').map(run => (
              <div key={run.doc_key} className="bg-zinc-900 border border-sky-800/50 rounded-lg p-3 md:p-4 mt-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-sky-400 animate-pulse" />
                    <span className="text-zinc-100 font-medium">
                      {(run as FixQaRunStatus & { language_name?: string }).language_name || langName(run.language_code)} {run.level}
                    </span>
                    <span className="text-xs text-zinc-500">
                      Stage {run.current_step + 1}/{run.total_steps}
                    </span>
                  </div>
                  <button onClick={() => handleGenCancel(run.doc_key)}
                    className="px-2.5 py-1 rounded text-xs font-medium bg-red-900/50 text-red-300 hover:bg-red-800/50">
                    Cancel
                  </button>
                </div>
                <div className="text-sm text-sky-300 mb-2">
                  {run.current_section} — {run.current_check}
                </div>
                <div className="w-full bg-zinc-700 rounded-full h-2 mb-2">
                  <div className="bg-sky-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${((run.current_step + 1) / run.total_steps) * 100}%` }} />
                </div>
                <div className="flex gap-4 text-xs text-zinc-500">
                  <span>Cost: ${run.cost_usd?.toFixed(4) ?? '0.0000'}</span>
                  {run.started_at && (
                    <span>Elapsed: {Math.round((Date.now() - new Date(run.started_at).getTime()) / 1000)}s</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* API Console */}
          {renderConsole()}

          {/* Generation history */}
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-zinc-300 mb-3">Generation History</h3>
            {genHistory.length === 0 ? (
              <p className="text-xs text-zinc-500">No generation runs yet.</p>
            ) : (
              <div className="space-y-1">
                {genHistory.map(run => {
                  const expanded = genExpandedRun === run.doc_key;
                  const runAny = run as FixQaRunStatus & { model?: string; language_name?: string; base_lang?: string; custom_instructions?: string; steps?: Array<{ sk: string; label: string; section: string; input_tokens: number; output_tokens: number; cost_usd: number; completed_at: string }> };
                  return (
                    <div key={run.doc_key + run.created_at}>
                      <div className="flex flex-wrap items-center gap-2 md:gap-3 px-2 md:px-3 py-2 rounded hover:bg-zinc-700/50 text-sm cursor-pointer"
                        onClick={() => setGenExpandedRun(expanded ? null : run.doc_key)}>
                        <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                          run.status === 'completed' ? 'bg-emerald-400' :
                          run.status === 'failed' ? 'bg-red-500' :
                          run.status === 'running' ? 'bg-sky-400 animate-pulse' :
                          run.status === 'cancelled' ? 'bg-zinc-500' : 'bg-amber-500'
                        }`} />
                        <span className="text-zinc-300 font-mono text-xs">{run.doc_key.replace('gen_', '')}</span>
                        <span className="text-zinc-500 text-xs">{run.status}</span>
                        <span className="text-zinc-600 text-xs">{run.current_step + 1}/{run.total_steps} stages</span>
                        <span className="text-zinc-600 text-xs font-mono">${run.cost_usd?.toFixed(4) ?? '0.0000'}</span>
                        {runAny.model && <span className="text-xs text-zinc-600">{runAny.model}</span>}
                        {runAny.base_lang && <span className="text-xs text-zinc-600">base: {BASE_LANG_LABELS[runAny.base_lang] ?? runAny.base_lang}</span>}
                        <span className="text-zinc-600 text-xs md:ml-auto">{run.created_at?.slice(0, 16)}</span>
                        {run.error && <span className="text-red-400 text-xs truncate max-w-xs">{run.error}</span>}
                      </div>
                      {expanded && (
                        <div className="ml-6 mb-2 border-l border-zinc-700 pl-3">
                          {runAny.custom_instructions && (
                            <div className="text-xs text-amber-400/80 mb-2 italic">{runAny.custom_instructions}</div>
                          )}
                          {run.error && (
                            <div className="text-xs text-red-400 mb-2 bg-red-900/20 border border-red-800/30 rounded px-2 py-1.5">
                              <span className="font-medium">Failed at:</span> {run.current_section || 'unknown'} ({run.current_check || 'unknown'})
                              <br /><span className="font-medium">Error:</span> {run.error}
                              {run.started_at && run.completed_at && (
                                <><br /><span className="font-medium">Duration:</span> {Math.round((new Date(run.completed_at).getTime() - new Date(run.started_at).getTime()) / 1000)}s</>
                              )}
                            </div>
                          )}
                          {runAny.steps && runAny.steps.length > 0 ? <table className="text-xs text-zinc-400 w-full">
                            <thead>
                              <tr className="text-zinc-500">
                                <th className="text-left py-0.5 pr-3">Step</th>
                                <th className="text-left py-0.5 pr-3">Section</th>
                                <th className="text-right py-0.5 pr-3">Input</th>
                                <th className="text-right py-0.5 pr-3">Output</th>
                                <th className="text-right py-0.5 pr-3">Cost</th>
                                <th className="text-left py-0.5 pr-3">Status</th>
                                <th className="text-right py-0.5">Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {runAny.steps.map((step, si) => {
                                const s = step as typeof step & { sk?: string; completed_at?: string; status?: string; error?: string };
                                const isFailed = s.status === 'failed';
                                return (
                                  <>
                                    <tr key={s.sk ?? si} className={`hover:bg-zinc-700/30 ${isFailed ? 'text-red-400' : ''}`}>
                                      <td className="py-0.5 pr-3 font-mono">{s.label}</td>
                                      <td className="py-0.5 pr-3">{s.section}</td>
                                      <td className="py-0.5 pr-3 text-right font-mono">{(s.input_tokens || 0).toLocaleString()}</td>
                                      <td className="py-0.5 pr-3 text-right font-mono">{(s.output_tokens || 0).toLocaleString()}</td>
                                      <td className="py-0.5 pr-3 text-right font-mono">${(s.cost_usd || 0).toFixed(4)}</td>
                                      <td className={`py-0.5 pr-3 ${isFailed ? 'text-red-400 font-medium' : 'text-emerald-500'}`}>{s.status ?? 'completed'}</td>
                                      <td className="py-0.5 text-right">{s.completed_at?.slice(11, 19) ?? '-'}</td>
                                    </tr>
                                    {isFailed && s.error && (
                                      <tr key={`${s.sk ?? si}-err`}>
                                        <td colSpan={7} className="py-0.5 pl-6 text-red-400/80 text-[11px]">{s.error}</td>
                                      </tr>
                                    )}
                                  </>
                                );
                              })}
                            </tbody>
                          </table> : !run.error && (
                            <div className="text-xs text-zinc-500">No step records.</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'documents' && <>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-zinc-100">CEFR Coverage Matrix</h1>
          <p className="text-zinc-400 text-sm mt-1">
            {allLangs.length} languages &middot; {filledCells}/{totalCells} cells &middot; {coveragePct}% coverage
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 md:gap-4">
          <button onClick={() => setShowNewLangDialog(true)}
            className="px-3 py-1.5 rounded text-xs font-medium bg-teal-800 text-teal-200 hover:bg-teal-700">
            + Generate New
          </button>
          <select
            value={matrixBaseLang ?? ''}
            onChange={e => setMatrixBaseLang(e.target.value || null)}
            className="px-2 py-1 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-xs font-mono focus:outline-none focus:border-teal-500"
          >
            <option value="">All base langs</option>
            {ALL_BASE_LANGS.map(b => (
              <option key={b.code} value={b.code}>{b.code} — {b.label}</option>
            ))}
          </select>
          <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400" /> Approved</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500/70" /> Draft</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-zinc-700" /> Missing</span>
          </div>
        </div>
      </div>

      {selectedCell && (
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 md:p-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-zinc-100 font-medium">
                {langName(selectedCell.lang)} &middot; {selectedCell.level}
              </span>
              {statusDot(cellStatus)}
              <span className="text-xs text-zinc-500">{cellStatus}</span>
              {renderBaseLangSelector()}
            </div>
            <div className="flex gap-2">
              {renderActionButtons()}
              <button onClick={() => { setSelectedCell(null); setDocContent(null); setPreflight(null); }}
                className="px-3 py-1.5 rounded text-xs text-zinc-400 hover:text-zinc-200">
                Close
              </button>
            </div>
          </div>
          {renderPreflightResult()}
          {renderDocPanel()}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="sticky left-0 bg-zinc-900 z-10 px-3 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-b border-zinc-700">
                Language
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-b border-zinc-700">
                Base
              </th>
              {filteredLevels.map((lvl) => (
                <th key={lvl} className={`px-3 py-2 text-center text-xs font-mono font-bold border-b border-zinc-700 ${LEVEL_COLORS[lvl] ?? 'text-zinc-400'}`}>
                  {lvl}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allLangs.map((lang) => {
              const contentBases = langBaseLangs.get(lang) || new Set(['en']);
              const visibleBases = matrixBaseLang
                ? (contentBases.has(matrixBaseLang) ? [matrixBaseLang] : [])
                : [...contentBases].sort((a, b) => a === 'en' ? -1 : b === 'en' ? 1 : a.localeCompare(b));
              if (visibleBases.length === 0) return null;
              return visibleBases.map((base, baseIdx) => (
                <tr key={`${lang}_${base}`} className={`hover:bg-zinc-800/50 ${baseIdx === visibleBases.length - 1 ? 'border-b border-zinc-800/50' : ''}`}>
                  {baseIdx === 0 && (
                    <td rowSpan={visibleBases.length} className="sticky left-0 bg-zinc-900 z-10 px-3 py-1.5 text-zinc-300 whitespace-nowrap align-top border-b border-zinc-800/50">
                      <span className="font-mono text-xs text-zinc-600 mr-2">{lang}</span>
                      {langName(lang)}
                    </td>
                  )}
                  <td className="px-3 py-1.5 text-xs text-zinc-500 font-mono">
                    {base}
                  </td>
                  {filteredLevels.map((lvl) => {
                    const cell = getCellState(lang, lvl, base);
                    const isSelected = selectedCell?.lang === lang && selectedCell?.level === lvl;
                    const docKey = base === 'en' ? `${lvl}_${lang}` : `${lvl}_${lang}__${base}`;
                    return (
                      <td key={`${lvl}_${base}`} onClick={() => handleCellClick(lang, lvl, base)}
                        className={`px-3 py-1.5 text-center cursor-pointer transition-colors ${isSelected ? 'bg-teal-900/30 ring-1 ring-teal-500/50' : 'hover:bg-zinc-700/30'}`}>
                        <div className="flex items-center justify-center gap-1.5">
                          {statusDot(cell.status)}
                          {renderRatingBadge(generatedDocs.get(docKey)?.rating)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>

      {renderConsole()}
      {renderFixModal()}
      {renderGenerateNewDialog()}

      </>}

      {pendingKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-zinc-800 border border-amber-600 rounded-xl p-4 md:p-6 max-w-lg w-full mx-4 shadow-2xl">
            <h2 className="text-lg font-bold text-amber-400 mb-3">Shrinkage Guard — Confirmation Required</h2>
            <p className="text-zinc-300 text-sm mb-4">
              The improved document for <span className="font-mono text-amber-200">{pendingKey}</span> is significantly smaller than the original.
              This usually means Claude's output was truncated. The smaller version has been saved as <em>pending</em> — the original is untouched.
            </p>
            <p className="text-zinc-400 text-xs mb-5">
              <strong>Confirm</strong> = replace original with the smaller version.{' '}
              <strong>Discard</strong> = throw away the smaller version and keep the original.
            </p>
            <div className="flex gap-3">
              <button onClick={async () => {
                const r = await apiCall('POST', `/api/admin/docs/${encodeURIComponent(pendingKey)}/confirm-pending`, {});
                if (r.ok) {
                  addLog({ type: 'info', message: `Pending content confirmed for ${pendingKey}` });
                  setPendingKey(null);
                  const d = await apiCall('GET', `/api/admin/docs/${encodeURIComponent(pendingKey)}`);
                  if (d.ok) { const data = d.data as { content?: string }; if (data.content) setDocContent(data.content); }
                }
              }} className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-amber-700 text-amber-100 hover:bg-amber-600 transition-colors">
                Confirm (use smaller)
              </button>
              <button onClick={async () => {
                const r = await apiCall('POST', `/api/admin/docs/${encodeURIComponent(pendingKey)}/discard-pending`, {});
                if (r.ok) {
                  addLog({ type: 'info', message: `Pending content discarded for ${pendingKey}` });
                  setPendingKey(null);
                }
              }} className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-zinc-600 text-zinc-200 hover:bg-zinc-500 transition-colors">
                Discard (keep original)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
